import { z } from 'zod';
/**
 * Контракты раздела «Запуски FB» (`/api/ads/*`) — шлюз admin-api к
 * конструктору запусков auto-ads (`/internal/meta-builder`). Единственный
 * источник истины, общий для роутов admin-api, порта `packages/auto-ads` и
 * модуля `admin-module-ads-launcher`. DTO 1:1 повторяют контракт K1 auto-ads
 * (план 2026-10-14 fb-launch-builder-v2) с префиксом `Ads`; wire-схемы
 * самого auto-ads живут в адаптере (`packages/auto-ads/src/builder/wire.ts`).
 *
 * ДЕНЬГИ — minor units (целое), ВРЕМЯ — ISO 8601 со смещением.
 *
 * Тела запросов — `.strict()` (и вложенные объекты тоже): опечатка в имени
 * поля на записи должна давать 400, а не молча теряться.
 *
 * Ответы — МЯГКИЕ: `z.object` без strict, лишние поля апстрима отбрасываются,
 * длины строк не проверяются. Необязательные поля K1 (`x?: T`) и поля
 * `T | null` — `.nullable().default(null)`: апстрим может прислать и
 * отсутствие, и `null` (старый auto-ads в окне выката, поле добавлено
 * позже), читатели проверяют `!= null`.
 */
// ---------------------------------------------------------------------------
// Общие кирпичи
// ---------------------------------------------------------------------------
/** Mongo ObjectId (24 hex) — id шаблона, кабинета, пачки, запуска, медиа. */
export const AdsId = z.string().regex(/^[a-f0-9]{24}$/, { message: 'Неверный идентификатор' });
/** sha256 содержимого файла (64 hex) — `assetId` медиатеки. */
export const AdsAssetId = z.string().regex(/^[a-f0-9]{64}$/, {
    message: 'Неверный идентификатор файла',
});
const Count = z.number().int().nonnegative();
const Minor = z.number().int();
/** Момент времени В ЗАПРОСЕ — строгий ISO 8601 со смещением. */
const IsoIn = z.string().datetime({ offset: true });
/**
 * Момент времени В ОТВЕТЕ — мягкий: любая строка, которую понимает
 * `Date.parse`. auto-ads проверяет время zod 3 (`+0700` без двоеточия для
 * него валиден, для zod 4 — нет): старый запуск с таким `startTime` не должен
 * ронять весь ответ в 502.
 */
const Iso = z.string().refine((v) => !Number.isNaN(Date.parse(v)), {
    message: 'Неверная дата',
});
/** Id в ответе: формат не проверяется — это забота апстрима. */
const RefId = z.string().min(1);
const opt = (s) => s.nullable().default(null);
/**
 * Поле, которое auto-ads добавляет позже (окно выката): нет, null или чужая
 * форма — null, а не 502 всего ответа.
 */
const soft = (s) => s
    .nullish()
    .catch(null)
    .transform((v) => (v === undefined ? null : v));
/**
 * Метка поля ответа, которое добавил «Конструктор FB v4» (спека 2026-10-29
 * §10.2): в JSON Schema поля — `"x-ads-v4": true`. Админка получает поле
 * как обычно; mcp-api до волны v4 (M-1…M-4) по метке скрывает его из ответов
 * модели и из документации MCP — снимок MCP не меняется, legacy `/mcp` без v4.
 */
export const ADS_V4_FIELD_META_KEY = 'x-ads-v4';
const v4 = (s) => s.meta({ [ADS_V4_FIELD_META_KEY]: true });
/**
 * Метка поля ответа «только для админки» (`"x-ads-admin-only": true`): русские
 * подписи для человека, которых модель не получает ни в одном варианте MCP
 * (тексты для модели — английские). mcp-api убирает такое поле из ответов и из
 * документации MCP всегда, даже если имя поля в другом месте открыто (`differences`
 * плана групп — `{ fieldKey, value }`, а снимок отличий запуска — с русским `label`).
 */
export const ADS_ADMIN_ONLY_FIELD_META_KEY = 'x-ads-admin-only';
const v4AdminOnly = (s) => s.meta({ [ADS_V4_FIELD_META_KEY]: true, [ADS_ADMIN_ONLY_FIELD_META_KEY]: true });
export const AdsSeverity = z.enum(['error', 'warning']);
/**
 * Степень проблемы ЗНАЧЕНИЯ (реестр / план): к ошибке и предупреждению
 * добавлено нейтральное `info` — замечание, которое не блокирует и не
 * предупреждает («Страны не исключаются», `country-exclusion-none`).
 */
export const AdsIssueSeverity = z.enum(['error', 'warning', 'info']);
/** Мягкое поле подробностей: нет или чужая форма — undefined (подробности не роняют замечание). */
const detail = (s) => s.optional().catch(undefined);
/**
 * Подробности замечания (новый auto-ads, настройки воронки): какая настройка
 * (`settingKey`), где в значении (`place`: само значение, «остальные» или
 * правило N, с 1), какие значения не подошли (`values`), предел (`limit`),
 * измерение условия (`dimension`). Каждое поле мягкое: чужая форма — нет поля.
 */
export const AdsValueIssueDetails = z.object({
    settingKey: detail(z.string()),
    place: detail(z.union([z.enum(['value', 'otherwise']), z.object({ rule: z.number().int() }).loose()])),
    values: detail(z.array(z.string())),
    limit: detail(z.number()),
    dimension: detail(z.string()),
    /** Номер копии (с 1, как «Копия N» в тексте) — ставка лесенки вне пределов (v4 §4.1). */
    copy: v4(detail(z.number().int().nonnegative())),
    /** Группа шаблона, о которой замечание (v4 §4). */
    groupKey: v4(detail(z.string())),
    /** Все группы шаблона свёрнутого замечания (одно правило в нескольких группах, v4 §4). */
    groupKeys: v4(detail(z.array(z.string()))),
    /**
     * `account-quota-low` (v4, AA-RC): оценка обращений к Meta по кабинету, его
     * квота в час, активные объявления, от которых она посчитана; бюджет пачки — в `limit`.
     */
    calls: v4(detail(z.number())),
    quotaPerHour: v4(detail(z.number())),
    activeAds: v4(detail(z.number())),
    /** Кабинет, о котором предупреждение `account-quota-low` (AA-RC); нет — undefined. */
    platformAccountId: v4(detail(z.string())),
});
/** Проблема значения (реестр/сверка). `message` — RU из реестра auto-ads. */
export const AdsValueIssue = z.object({
    /** null — проблема не привязана к полю (например, к пачке целиком). */
    fieldKey: z.string().nullable(),
    code: z.string().min(1),
    severity: AdsIssueSeverity,
    message: z.string(),
    launchIndex: opt(z.number().int()),
    itemIndex: opt(z.number().int()),
    /**
     * Одна и та же проблема в нескольких запусках пачки — одной записью со
     * списком индексов (новый auto-ads). Старый — по записи на запуск
     * (`launchIndex`); UI сводит такие дубли сам. Мягко: не массив — null.
     */
    launchIndexes: z.array(z.number().int()).nullish().catch(null),
    /**
     * Род замечания шаблона: `unknown-field` / `obsolete` — шаблон разошёлся с
     * реестром, `advice` — совет (предупреждение, а не ошибка), `invalid` — значение не
     * прошло проверку (400 `invalid-values` при сохранении). Строка, а не enum:
     * новое значение не роняет ответ. Нет поля (старый auto-ads) — UI выводит род сам.
     */
    kind: z.string().nullish().catch(null),
    /** Подробности (настройки воронки); нет (старый auto-ads) — null. */
    details: AdsValueIssueDetails.nullish().catch(null),
    /**
     * Группа шаблона, к которой замечание (v4 §3.5): RU-текст начинается с
     * «Группа шаблона «LABEL»: …» — UI меняет префикс на «① LABEL». Нет — null.
     */
    groupKey: v4(z.string().nullish().catch(null)),
});
/** Строка «поле — подпись — значение для показа» (сводки шаблона, таргетинг preview). */
export const AdsFieldDisplay = z.object({
    fieldKey: z.string(),
    label: z.string(),
    display: z.string(),
    /**
     * То же значение словами для человека (подпись опции реестра, имя списка
     * стран), новый auto-ads (аудит MCP I4); `display` остаётся кодом — admin-web
     * переводит его сам. Нет — нет поля (необязательное: строки сводки строит и
     * сам UI, например в тестах и превью).
     */
    displayLabel: z.string().nullish().catch(null),
});
/**
 * Ошибка шага / запуска / файла: `code` у Meta бывает числом — приводится к
 * строке. Шаг запуска несёт журнал Meta (`userMsg` — локализованный текст
 * для человека, `message` — технический англ.): `message` на выходе —
 * `userMsg ?? message`; `fbtraceId` — для обращения в поддержку Meta.
 */
export const AdsErrorInfo = z
    .object({
    code: z.union([z.string(), z.number()]).transform(String),
    message: opt(z.string()),
    userMsg: opt(z.string()),
    fbtraceId: opt(z.string()),
    /**
     * Исходный (технический) текст ошибки, когда `message` — фраза для
     * человека (ошибка переноса файла, новый auto-ads). Для «Подробнее».
     */
    detail: soft(z.string()),
})
    .transform(({ code, message, userMsg, fbtraceId, detail }) => ({
    code,
    message: userMsg ?? message,
    fbtraceId,
    detail,
}));
/** Курсорная страница K1: `nextCursor` отсутствует/null — дальше нет. */
const cursorPage = (item) => z.object({
    items: z.array(item),
    nextCursor: opt(z.string().min(1)),
});
// ---------------------------------------------------------------------------
// Статусы
// ---------------------------------------------------------------------------
/**
 * Статус запуска для показа (выводится auto-ads из state + activation +
 * verify). RU-подписи — `ui-domain/status-labels.ts`.
 */
export const ADS_LAUNCH_DISPLAY_STATUSES = [
    'draft',
    'scheduled',
    'creating',
    'ready_paused',
    'blocked',
    'activating',
    'waiting_start',
    'running',
    /**
     * Не создан: пачку отменили до его создания (`cancelledAt`). Не «Черновик» —
     * его уже не выполнить. Старый auto-ads такого статуса не присылает.
     */
    'cancelled',
    /**
     * Включён и поставлен на паузу конструктором — человеком (`POST /pause`) или
     * системой при отмене пачки до старта (`pauseInfo.source`); без подмены и тревог.
     */
    'paused',
    'paused_on_drift',
    /** Подмена требовала паузы, но PAUSED не прошёл (auto-ads повторяет) — нужен человек. */
    'pause_failed',
    /** Подмена (или несверяемый ответ) после старта: автопаузы нет — нужен человек. */
    'needs_attention',
    'failed',
    'rolling_back',
    'rolled_back',
];
export const AdsLaunchDisplayStatus = z.enum(ADS_LAUNCH_DISPLAY_STATUSES);
/**
 * `displayStatus` в ОТВЕТЕ — мягкий: известный статус ИЛИ любая непустая
 * строка. Новый статус auto-ads не роняет запуск/пачку в 502 — экран
 * показывает его как есть нейтральной плашкой.
 */
export const AdsLaunchDisplayStatusIn = z.union([AdsLaunchDisplayStatus, z.string().min(1)]);
/** Действия над запуском, которые разрешает auto-ads (`availableActions`). */
export const ADS_LAUNCH_ACTIONS = [
    'activate',
    'force_activate',
    'pause',
    'reverify',
    /** `POST /launches/:id/execute` (у нас — `/retry`). */
    'retry',
    'rollback',
    /**
     * Включить СЕЙЧАС запуск, чей старт прошёл, пока он ждал включения (v4 §8.1):
     * `activate` с `acknowledgeLateStart: true` («Включить сейчас»).
     */
    'activate_late',
    /** То же при расхождениях сверки: ещё и `force` («Включить сейчас всё равно», владелец). */
    'force_activate_late',
];
export const ADS_BATCH_STATES = [
    'draft',
    'running',
    'needs_attention',
    'failed',
    'active',
    'done_manual',
    'cancelled',
    /** Все созданные запуски пачки откачены (объекты удалены из Meta). */
    'rolled_back',
];
export const AdsBatchState = z.enum(ADS_BATCH_STATES);
/**
 * `state` пачки в ОТВЕТЕ — мягкий: известное состояние ИЛИ любая непустая
 * строка (новое состояние auto-ads показывается как есть, а не роняет 502).
 */
export const AdsBatchStateIn = z.union([AdsBatchState, z.string().min(1)]);
export const AdsActivationMode = z.enum(['auto', 'manual']);
export const AdsActivationStatus = z.enum([
    'pending',
    'done',
    'blocked_verify',
    'paused_on_drift',
    'skipped',
    'pause_failed',
    'needs_attention',
]);
export const AdsBatchMode = z.enum(['execute', 'draft']);
export const AdsBatchSource = z.enum(['admin', 'agent']);
export const AdsBatchLayout = z.enum(['campaign_per_item', 'adsets_in_campaign', 'ads_in_adset']);
// ---------------------------------------------------------------------------
// GET /health
// ---------------------------------------------------------------------------
/**
 * `configured: false` — admin-api запущен без `AUTO_ADS_API_BASE_URL` /
 * `AUTO_ADS_API_TOKEN`; остальные поля тогда null.
 */
export const AdsHealthResponse = z.object({
    configured: z.boolean(),
    enabled: opt(z.boolean()),
    /** Стенд с fake-Meta — UI показывает баннер. */
    fakeMeta: opt(z.boolean()),
    graphVersion: opt(z.string()),
    registryVersion: opt(z.string()),
    uploadPartSize: opt(Count),
    mediaMaxBytes: opt(z.object({ image: Count, video: Count })),
    /**
     * Что этот auto-ads умеет сейчас (v4 §10.4, Д-1) — вне хэшируемого реестра.
     * `builderV4` — признак кода v4 (повтор пачки, расчёт денег, включение после
     * старта, лесенка): нет — v4-элементы UI скрыты. `adSetGroups` — приём групп
     * шаблона включён (`META_BUILDER_ADSET_GROUPS_ENABLED`), `adSetGroupsAgent` —
     * и для нейронки; `maxGroupAdSetsPerBatch` — потолок групп объявлений в
     * пачке с группами. Нет `features` (старый auto-ads, admin-api без
     * конфигурации) — null; нет отдельного признака — false / 0.
     */
    features: v4(soft(z.object({
        builderV4: z.boolean().catch(false),
        // признаки групп помечены отдельно: MCP открывает `features` раньше групп (M-1 → M-4)
        adSetGroups: v4(z.boolean().catch(false)),
        adSetGroupsAgent: v4(z.boolean().catch(false)),
        maxGroupAdSetsPerBatch: v4(Count.catch(0)),
    }))),
});
// ---------------------------------------------------------------------------
// GET /registry
// ---------------------------------------------------------------------------
export const AdsFieldGroup = z.enum([
    'campaign',
    'adset',
    'targeting',
    'creative',
    'schedule',
    'copies',
    'naming',
]);
export const AdsFieldLevel = z.enum(['campaign', 'adset', 'ad', 'creative', 'account', 'batch']);
/** Известные форматы значения по `type` — таблица K1 «Форматы значений». */
export const ADS_FIELD_TYPES = [
    'enum',
    'money',
    'int',
    'decimal',
    'int_range',
    'bool',
    'text',
    'textarea',
    'url',
    'country_list',
    'country_codes',
    'locales',
    'audience_slots',
    'interests',
    'start',
    'creative_items',
    'int_list',
    'geo',
    // структурные значения со своим виджетом (auto-ads, 20.10 — без JSON для людей)
    'daily_quota',
    'genders',
    'placements',
    'special_ad_categories',
    'audience_relaxation',
    'advantage_levers',
    'creative_features',
    'regional_regulation',
    'value_rules',
    'json',
];
export const AdsKnownFieldType = z.enum(ADS_FIELD_TYPES);
/**
 * `type` дескриптора — МЯГКИЙ: известный формат ИЛИ любая непустая строка.
 * Новый тип поля в auto-ads не должен ронять весь `/registry` в 502 — экран
 * показывает поле неизвестного типа как «Поле пока нельзя настроить здесь» со
 * значением словами (не JSON).
 */
export const AdsFieldType = z.union([AdsKnownFieldType, z.string().min(1)]);
/** Кто задаёт значение: зашито / профиль кабинета / шаблон / запуск. */
export const AdsFieldMode = z.enum(['fixed', 'account', 'template', 'launch']);
export const AdsFieldOption = z.object({
    value: z.union([z.string(), z.number(), z.boolean()]),
    label: z.string(),
    hint: opt(z.string()),
});
export const AdsFieldConstraints = z.object({
    required: opt(z.boolean()),
    min: opt(z.number()),
    max: opt(z.number()),
    step: opt(z.number()),
    maxItems: opt(Count),
    pattern: opt(z.string()),
    mustContain: opt(z.array(z.string())),
});
export const AdsFieldDescriptor = z.object({
    key: z.string().min(1),
    label: z.string(),
    hint: opt(z.string()),
    group: AdsFieldGroup,
    order: z.number(),
    level: AdsFieldLevel,
    /** Путь в объекте Meta; null — поле только наше (раскладка, копии). */
    metaPath: z.string().nullable(),
    type: AdsFieldType,
    mode: AdsFieldMode,
    launchEditableDefault: z.boolean(),
    /**
     * Шаблон оставил поле пустым — оно становится полем запуска и обязательно,
     * если видно (ставка, минимальный ROAS). Старый auto-ads — false.
     */
    requiredAtLaunchWhenEmpty: z.boolean().default(false),
    default: z.unknown(),
    fixedValue: z.unknown(),
    fixedDisplay: opt(z.string()),
    options: opt(z.array(AdsFieldOption)),
    constraints: opt(AdsFieldConstraints),
    visibleWhen: opt(z.object({ key: z.string(), in: z.array(z.unknown()) })),
    /**
     * Зависимое значение по умолчанию (auto-ads, решение 172): пока поле не
     * задано явно, при `key` ∈ `in` его умолчание — `value` (окно атрибуции
     * «Клик 7 дн.» при оптимизации «Ценность (pLTV)»). Старый auto-ads — нет поля.
     */
    defaultWhen: opt(z.array(z.object({
        key: z.string(),
        in: z.array(z.unknown()),
        value: z.unknown(),
        /** Строка для редактора при автопереключении (auto-ads #936); нет — общий текст. */
        note: z.string().optional(),
    }))),
    /** null — поле не сверяется после создания. */
    verify: z.object({ severity: AdsSeverity }).nullable(),
    enabled: z.boolean(),
    /**
     * Значение задаётся на КАЖДЫЙ креатив (тексты объявления), а не на запуск:
     * в `values` шаблона и в `overrides` пачки такого ключа нет.
     */
    perItem: z.boolean().default(false),
    /**
     * Поле можно задать в группе шаблона (v4 §3.2): значение группы главнее
     * общего для её групп объявлений. Старый auto-ads — false.
     */
    groupable: v4(z.boolean().catch(false)),
    source: opt(z.string()),
});
export const AdsLocalePreset = z.object({
    key: z.string(),
    label: z.string(),
    localeIds: z.array(z.number().int()),
});
export const AdsGuaranteeDescriptor = z.object({
    key: z.string(),
    /** Что подменяет Meta («Анимация») — название угрозы. */
    label: z.string(),
    /**
     * Гарантия утверждением («Анимация выключена») — её и показывает экран.
     * Нет (старый auto-ads) — null, экран показывает `label`.
     */
    text: soft(z.string()),
    detail: z.string(),
    verify: z.enum(['error', 'warning', 'none']),
});
export const AdsRegistryResponse = z.object({
    version: z.string(),
    fields: z.array(AdsFieldDescriptor),
    /** Параметры пачки (группа `copies`): раскладка, копии, правила, старт и создание. */
    options: z.array(AdsFieldDescriptor),
    localePresets: z.array(AdsLocalePreset),
    bannedLocaleIds: z.array(z.number().int()),
    guarantees: z.array(AdsGuaranteeDescriptor),
    namingTokens: z.array(z.object({ token: z.string(), label: z.string() })),
    /**
     * Шаблоны имён по умолчанию (без префикса) — сервера, а не копия в UI:
     * форма нового шаблона берёт их отсюда (v4 D11a). Старый auto-ads — null
     * (поля имён тогда пустые и обязательные).
     */
    namingDefaults: soft(z.object({ campaign: z.string(), adset: z.string(), ad: z.string() })),
    /**
     * Пределы групп шаблона (v4 §3.2) — статично, часть версии реестра: экран и
     * MCP берут пределы отсюда, не зашивают в тексты. Старый auto-ads — null.
     */
    adSetGroupRules: v4(soft(z.object({
        templateMin: Count,
        max: Count,
        labelPattern: z.string(),
        labelMax: Count,
        maxAdSetsPerCampaign: Count,
    }))),
});
// ---------------------------------------------------------------------------
// Шаблоны — /templates
// ---------------------------------------------------------------------------
export const AdsTemplateKind = z.enum(['test', 'scale', 'creative_test']);
export const ADS_TEMPLATE_NAME_MAX = 120;
export const ADS_TEMPLATE_DESCRIPTION_MAX = 1000;
const ValuesRecord = z.record(z.string(), z.unknown());
/** Префикс ключей опций шаблона в реестре auto-ads (`opt.copies`, `opt.layout`, …). */
export const ADS_OPTION_KEY_PREFIX = 'opt.';
/**
 * Ключи опций шаблона → полная форма реестра (`copies` → `opt.copies`).
 * auto-ads принимает и хранит обе формы (сид-шаблоны записаны короткими
 * ключами, UI пишет полные), а при чтении полный ключ главнее короткого —
 * здесь то же правило, чтобы экран видел ровно то, что применит auto-ads.
 */
export function normalizeAdsOptionKeys(options) {
    const out = {};
    for (const [k, v] of Object.entries(options)) {
        if (!k.startsWith(ADS_OPTION_KEY_PREFIX))
            out[`${ADS_OPTION_KEY_PREFIX}${k}`] = v;
    }
    for (const [k, v] of Object.entries(options)) {
        if (k.startsWith(ADS_OPTION_KEY_PREFIX))
            out[k] = v;
    }
    return out;
}
// ---------------------------------------------------------------------------
// Группы шаблона (спека «Конструктор FB v4» §3.5, §10.1; решения [Р] 48, 50)
//
// Шаблон задаёт варианты группы объявлений («группы шаблона», ключи `g1…`);
// каждая выбранная группа становится своей группой объявлений. Шаблоны и
// пределы — те же, что у auto-ads (`registry/adset-groups.ts`): позитивный
// латинский шаблон названия (в Meta не уходит кириллица), ключ выдаёт сервер.
// ---------------------------------------------------------------------------
/**
 * Название группы шаблона: латиница, цифры, пробел и `_ . , % + $ ( ) / -`,
 * 1–40 знаков. Сверх шаблона — без пробелов по краям и хотя бы одна буква или
 * цифра (`isAdsGroupLabelValid`). `.source` совпадает с `adSetGroupRules.labelPattern`.
 */
// eslint-disable-next-line no-useless-escape -- `\/` как в спеке §3.5: `.source` = `labelPattern` auto-ads
export const ADS_GROUP_LABEL_RE = /^[A-Za-z0-9 _.,%+$()\/-]{1,40}$/;
/** Ключ группы шаблона — выдаёт auto-ads (`g1…g999`), не переиспользуется. */
export const ADS_GROUP_KEY_RE = /^g[1-9][0-9]{0,2}$/;
/** Пределы групп (auto-ads `GROUP_LIMITS`; экран берёт их из `adSetGroupRules` реестра). */
export const ADS_GROUP_LIMITS = {
    templateMin: 2,
    max: 10,
    labelMax: 40,
    maxAdSetsPerCampaign: 50,
};
/** Название проходит всё правило §3.5: шаблон, без пробелов по краям, есть буква или цифра. */
export const isAdsGroupLabelValid = (label) => ADS_GROUP_LABEL_RE.test(label) && label.trim() === label && /[A-Za-z0-9]/.test(label);
const ADS_GROUP_LABEL_MESSAGE = 'Название группы — только латиница, цифры, пробел и _ . , % + $ ( ) / -, до 40 знаков, без пробелов по краям';
const AdsGroupKeyIn = z.string().regex(ADS_GROUP_KEY_RE, { message: 'Неверный ключ группы' });
/** Одна группа во входе шаблона: `key` нет — новая группа (ключ выдаст auto-ads). */
export const AdsAdSetGroupInput = z
    .object({
    key: AdsGroupKeyIn.optional(),
    label: z.string().refine(isAdsGroupLabelValid, { message: ADS_GROUP_LABEL_MESSAGE }),
    /** Только поля реестра с `groupable: true` (проверяет auto-ads). */
    values: z.record(z.string(), z.unknown()),
})
    .strict();
/**
 * `adSetGroups` во входе шаблона: 2–10 групп, ключи и названия (без учёта
 * регистра) не повторяются. Убрать группы — `null` (PUT без поля — оставить).
 */
export const AdsAdSetGroupsInput = z
    .object({
    groups: z
        .array(AdsAdSetGroupInput)
        .min(ADS_GROUP_LIMITS.templateMin, {
        message: 'Одна группа — то же самое, что без групп. Добавьте вторую или уберите группы',
    })
        .max(ADS_GROUP_LIMITS.max, { message: 'Не больше 10 групп в шаблоне' })
        .refine((gs) => {
        const keys = gs.flatMap((g) => (g.key === undefined ? [] : [g.key]));
        return new Set(keys).size === keys.length;
    }, { message: 'Ключи групп повторяются' })
        .refine((gs) => new Set(gs.map((g) => g.label.toLowerCase())).size === gs.length, {
        message: 'Названия групп повторяются',
    }),
})
    .strict();
/**
 * Группы шаблона во входе ПАЧКИ ([Р] 48 — не список групп): `keys` — какие
 * группы шаблона создать (нет — все; 0–10 без повторов; пустой список
 * доходит до auto-ads и получает человеческий `groups-all-removed`), `edits` —
 * ставка группы (если шаблон разрешает менять ставку при запуске) и сумма ABO.
 * Добавить группу при запуске нельзя — только в шаблоне.
 */
export const AdsBatchAdSetGroupsInput = z
    .object({
    keys: z
        .array(AdsGroupKeyIn)
        .max(ADS_GROUP_LIMITS.max, { message: 'Не больше 10 групп' })
        .refine((ks) => new Set(ks).size === ks.length, { message: 'Группы повторяются' })
        .optional(),
    edits: z
        .record(AdsGroupKeyIn, z
        .object({
        bidMinor: z
            .number()
            .int()
            .min(100, { message: 'Ставка — не меньше 1,00' })
            .max(100_000, { message: 'Ставка — не больше 1 000,00' })
            .optional(),
        dailyBudgetMinor: z
            .number()
            .int()
            .min(100, { message: 'Бюджет группы — не меньше 1,00' })
            .optional(),
    })
        .strict())
        .optional(),
})
    .strict();
/** Группа шаблона в ответе (`key`, `label`, отличия `values`). */
export const AdsAdSetGroup = z.object({
    key: z.string(),
    label: z.string(),
    values: z.record(z.string(), z.unknown()),
});
/** Группы шаблона в ответе (`TemplatesService.view`); `lastKeyN` сервер наружу не отдаёт. */
export const AdsAdSetGroups = z.object({ groups: z.array(AdsAdSetGroup) });
export const AdsNamingInput = z
    .object({
    prefix: z
        .string()
        .min(1)
        .max(20)
        .regex(/^[A-Za-z0-9_-]+$/, { message: 'Префикс — латиница, цифры, «_» и «-»' }),
    campaign: z.string().min(1).max(200),
    adset: z.string().min(1).max(200),
    ad: z.string().min(1).max(200),
    /**
     * Имя копии кампании (шаблон-копия, K2): подменяет шаблон `campaign`. Сервер хранит его,
     * поэтому правка шаблона должна отправлять его обратно, иначе имя пропадёт.
     */
    campaignOverride: z
        .string()
        .min(1)
        .max(120)
        .refine((v) => !/[{}]/.test(v), { message: 'В имени нельзя использовать { и }' })
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Настройки воронки (спека 2026-10-24 campaign-funnel-settings §11, план K6)
//
// Не поле реестра Meta: в Meta не уходят, хранятся рядом с `values`/`options`
// шаблона. Ключи и допустимые значения — из каталога настроек
// (`GET /funnel-settings/catalog`); смысл значений проверяет auto-ads по
// каталогу (при сохранении шаблона и предпросмотре пачки).
// ---------------------------------------------------------------------------
export const ADS_FUNNEL_SETTINGS_MAX_KEYS = 50;
export const ADS_FUNNEL_SETTING_KEY_MAX = 100;
export const ADS_FUNNEL_SETTING_STRING_MAX = 500;
/** Ключ настройки воронки (`paywall.layout`, `pricing.config`, …). */
const FunnelSettingKey = z
    .string()
    .min(1)
    .max(ADS_FUNNEL_SETTING_KEY_MAX, {
    message: `Ключ настройки — не длиннее ${ADS_FUNNEL_SETTING_KEY_MAX} символов`,
});
/** Обычное значение настройки: вариант (enum / funnelConfig) — строка, переключатель — boolean, число. */
export const AdsFunnelSettingScalar = z.union([
    z.string().max(ADS_FUNNEL_SETTING_STRING_MAX, {
        message: `Значение настройки — не длиннее ${ADS_FUNNEL_SETTING_STRING_MAX} символов`,
    }),
    z.number(),
    z.boolean(),
]);
// --- Значение-список (спека 2026-10-26 «Варианты триала») --------------------
//
// Вид каталога `trialPlans`: значение — набор строк (ключи тарифов). Это
// МНОЖЕСТВО: порядок и повторы не значимы, каноническая форма — без повторов,
// отсортирована (`ads-funnel-value.ts`, как в auto-ads). Входная схема —
// структурная, как в auto-ads: пустой список и повторы проходят, потолок
// размера щедрее смыслового; смысл (1..20 элементов из `options` каталога)
// проверяет auto-ads, форма админки — сама у себя.
/** Смысловые лимиты списка (проверяет auto-ads по каталогу; форма — у себя). */
export const ADS_FUNNEL_LIST_MAX_ITEMS = 20;
export const ADS_FUNNEL_LIST_ITEM_MAX_LENGTH = 200;
/** Структурный потолок — как в auto-ads (выше смыслового: «слишком много» — понятной причиной). */
const STRUCT_MAX_LIST_ITEMS = 50;
/** Значение-список: строки ≤ 200 символов, не больше 50 (структурно). */
export const AdsFunnelSettingList = z
    .array(z
    .string()
    .min(1, { message: 'Элемент списка не может быть пустым' })
    .max(ADS_FUNNEL_LIST_ITEM_MAX_LENGTH, {
    message: `Элемент списка — не длиннее ${ADS_FUNNEL_LIST_ITEM_MAX_LENGTH} символов`,
}))
    .max(STRUCT_MAX_LIST_ITEMS, {
    message: `В списке — не больше ${STRUCT_MAX_LIST_ITEMS} значений`,
});
/** Значение правила / «остальных»: обычное или список (правила не вкладываются). */
export const AdsFunnelSettingPlain = z.union([
    ...AdsFunnelSettingScalar.options,
    AdsFunnelSettingList,
]);
// --- Значение с условиями (спека 2026-10-26 §3.1, план C1) -------------------
//
// `{ rules: [{ when, value }], otherwise? }`: первое правило, у которого
// совпали ВСЕ указанные измерения (внутри измерения — любое значение), даёт
// значение; ни одно — `otherwise`, нет его — «не задано». Разрешает воронка /
// app-service; auto-ads и админка только хранят и проверяют.
//
// Входная схема — СТРУКТУРНАЯ, как в auto-ads (`funnel-setting-value.ts`):
// только форма и щедрые потолки размера. Смысл (правил 1..20, стран ≤ 250,
// измерения и значения из каталога, `targetable`) проверяет auto-ads по
// каталогу и отвечает понятной причиной. Иначе значение, которое админка не
// умеет редактировать (новое измерение каталога, страна вне её справочника) и
// отправляет обратно как получила, мешало бы сохранить шаблон при правке
// СОСЕДНЕЙ настройки. Что редактирует форма — проверяет строго сама форма.
//
// Объекты правила и значения — `loose()`: поле, которое добавит будущий
// auto-ads, при повторной отправке не теряется (решает о нём auto-ads).
// Ключи `__proto__` / `constructor` / `prototype` — отказ на любом уровне.
/** Смысловые лимиты (проверяет auto-ads по каталогу; форма — у себя). */
export const ADS_FUNNEL_TARGETED_MAX_RULES = 20;
export const ADS_FUNNEL_TARGETED_MAX_COUNTRIES = 250;
/** Структурные потолки — как в auto-ads (выше — тело отклоняется как неверное). */
const STRUCT_MAX_RULES = 50;
const STRUCT_MAX_DIMENSIONS = 10;
const STRUCT_MAX_DIMENSION_VALUES = 300;
const STRUCT_MAX_DIMENSION_VALUE_LENGTH = 32;
/** Возрастные диапазоны (как у Meta и модели окупаемости). */
export const ADS_FUNNEL_AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
export const AdsFunnelAgeRange = z.enum(ADS_FUNNEL_AGE_RANGES);
export const ADS_FUNNEL_GENDERS = ['female', 'male', 'other'];
export const AdsFunnelGender = z.enum(ADS_FUNNEL_GENDERS);
/** Измерения условий, которые знает админка (`dimensions[].key` каталога). */
export const ADS_FUNNEL_DIMENSION_KEYS = ['countries', 'ageRanges', 'genders'];
/** Ключи, которые никогда не бывают ключом условия и не копируются канонической формой. */
export const ADS_FUNNEL_UNSAFE_KEYS = new Set([
    '__proto__',
    'constructor',
    'prototype',
]);
/** Ключ измерения условия: простой идентификатор (без `$gt`, `a.b`, `__proto__`). */
const WHEN_KEY_RE = /^[A-Za-z][A-Za-z0-9]*$/;
/**
 * Объект без опасных ключей. `__proto__` из JSON у `loose()`-объекта zod
 * превращает в прототип результата — поэтому смотрим и на прототип.
 */
const noUnsafeKeys = (o) => Object.getPrototypeOf(o) === Object.prototype &&
    !Object.keys(o).some((k) => ADS_FUNNEL_UNSAFE_KEYS.has(k));
const UNSAFE_KEY = { message: 'Недопустимый ключ в значении настройки' };
/** Условие правила: измерение → значения (что за измерения и значения — решает каталог). */
export const AdsFunnelTargetedWhen = z
    .record(z
    .string()
    .min(1)
    .max(50)
    .regex(WHEN_KEY_RE, { message: 'Измерение условия — латинский идентификатор' })
    .refine((k) => !ADS_FUNNEL_UNSAFE_KEYS.has(k), UNSAFE_KEY), z.array(z.string().max(STRUCT_MAX_DIMENSION_VALUE_LENGTH)).max(STRUCT_MAX_DIMENSION_VALUES, {
    message: `В условии — не больше ${STRUCT_MAX_DIMENSION_VALUES} значений`,
}))
    .refine((w) => Object.keys(w).length <= STRUCT_MAX_DIMENSIONS, {
    message: `В условии — не больше ${STRUCT_MAX_DIMENSIONS} измерений`,
});
export const AdsFunnelTargetedRule = z
    .object({ when: AdsFunnelTargetedWhen, value: AdsFunnelSettingPlain })
    .loose()
    .refine(noUnsafeKeys, UNSAFE_KEY);
/** Значение с условиями: правила по порядку + «во всех остальных случаях». */
export const AdsFunnelTargetedValue = z
    .object({
    rules: z.array(AdsFunnelTargetedRule).max(STRUCT_MAX_RULES, {
        message: `Правил — не больше ${STRUCT_MAX_RULES}`,
    }),
    otherwise: AdsFunnelSettingPlain.optional(),
})
    .loose()
    .refine(noUnsafeKeys, UNSAFE_KEY);
/** Значение настройки в запросе: обычное, список или с условиями (план C1). */
export const AdsFunnelSettingValue = z.union([
    ...AdsFunnelSettingScalar.options,
    AdsFunnelSettingList,
    AdsFunnelTargetedValue,
]);
const withinKeyLimit = (r) => Object.keys(r).length <= ADS_FUNNEL_SETTINGS_MAX_KEYS;
const TOO_MANY_KEYS = {
    message: `Настроек воронки — не больше ${ADS_FUNNEL_SETTINGS_MAX_KEYS}`,
};
/**
 * Настройки воронки шаблона (запрос): значения по ключу каталога и ключи,
 * которые можно поменять при запуске (`funnelSettings` пачки).
 */
export const AdsTemplateFunnelSettingsInput = z
    .object({
    values: z.record(FunnelSettingKey, AdsFunnelSettingValue).refine(withinKeyLimit, TOO_MANY_KEYS),
    launchEditable: z.array(FunnelSettingKey).max(ADS_FUNNEL_SETTINGS_MAX_KEYS),
})
    .strict();
/**
 * Переопределения пачки: только ключи `launchEditable` шаблона; `null` —
 * убрать значение шаблона для этого запуска.
 */
export const AdsBatchFunnelSettingsInput = z
    .record(FunnelSettingKey, AdsFunnelSettingValue.nullable())
    .refine(withinKeyLimit, TOO_MANY_KEYS);
// --- Строгий вход для агента (MCP, ревью 2026-10-26 F5) ----------------------
//
// Та же структура, но правило и значение с правилами — `strict()`: модель не
// возвращает чужих полей, а придуманное ею (`priority`, `else`) иначе молча
// ушло бы в auto-ads. Лишнее поле — понятная ошибка ввода с именем ключа.
// Админка пользуется `loose()`-схемами выше.
/** Правило строго: только `{ when, value }`. */
export const AdsFunnelTargetedRuleStrict = z.strictObject({
    when: AdsFunnelTargetedWhen,
    value: AdsFunnelSettingPlain,
});
/** Значение с правилами строго: только `{ rules, otherwise? }`. */
export const AdsFunnelTargetedValueStrict = z.strictObject({
    rules: z.array(AdsFunnelTargetedRuleStrict).max(STRUCT_MAX_RULES, {
        message: `rules: at most ${STRUCT_MAX_RULES}`,
    }),
    otherwise: AdsFunnelSettingPlain.optional(),
});
/**
 * Ошибка union'а значения для агента: у объекта (значение с правилами) —
 * причина из его ветки с путём (`rules.0: unknown field "priority"`), а не
 * безликое «Invalid input» (EN — текст для модели).
 */
const strictValueError = (iss) => {
    const input = iss.input;
    if (iss.code !== 'invalid_union' ||
        input === null ||
        typeof input !== 'object' ||
        Array.isArray(input)) {
        return undefined;
    }
    const branch = iss.errors?.[iss.errors.length - 1];
    const first = branch?.[0];
    if (!first)
        return undefined;
    const at = (first.path ?? []).map(String).join('.');
    const why = first.code === 'unrecognized_keys'
        ? `unknown field ${(first.keys ?? []).map((k) => `"${k}"`).join(', ')} (a value with rules is { rules: [{ when, value }], otherwise? })`
        : (first.message ?? 'invalid');
    return at ? `${at}: ${why}` : why;
};
/** Значение настройки строго (вход агента). */
export const AdsFunnelSettingValueStrict = z.union([...AdsFunnelSettingScalar.options, AdsFunnelSettingList, AdsFunnelTargetedValueStrict], { error: strictValueError });
/** `funnelSettings` шаблона строго (вход агента). */
export const AdsTemplateFunnelSettingsStrictInput = z.strictObject({
    values: z
        .record(FunnelSettingKey, AdsFunnelSettingValueStrict)
        .refine(withinKeyLimit, TOO_MANY_KEYS),
    launchEditable: z.array(FunnelSettingKey).max(ADS_FUNNEL_SETTINGS_MAX_KEYS),
});
/** `funnelSettings` пачки строго (вход агента). */
export const AdsBatchFunnelSettingsStrictInput = z
    .record(FunnelSettingKey, AdsFunnelSettingValueStrict.nullable())
    .refine(withinKeyLimit, TOO_MANY_KEYS);
const FunnelScalarOut = z.union([z.string(), z.number(), z.boolean()]);
/** Список в ответе — без проверки длин (забота auto-ads). */
const FunnelListOut = z.array(z.string());
const FunnelPlainOut = z.union([FunnelScalarOut, FunnelListOut]);
/**
 * Значение с условиями в ОТВЕТЕ — мягкое: измерение или значение, которых
 * админка ещё не знает (новое в каталоге), не роняют ответ — экран называет
 * такое условие честно («условие, которого нет в списке»).
 */
// `loose()` (= `.passthrough()` zod 3) у правила и значения: поле, которое
// добавит будущий auto-ads, доходит до экрана и уходит обратно при повторной
// отправке из админки (её входная схема тоже `loose()`), а не теряется молча.
// Вход агента (MCP) — строгий (`AdsFunnelSettingValueStrict`): модель такие
// поля не возвращает, лишнее поле у неё — ошибка ввода. MCP отдаёт модели
// ответ по этой схеме, поэтому чужое поле она видит, но обратно его не пошлёт.
export const AdsFunnelTargetedValueOut = z
    .object({
    rules: z.array(z
        .object({
        // `.optional()` — мягко к пропуску значения измерения (ответ не роняем)
        when: z.record(z.string(), z.array(z.string()).optional()),
        value: FunnelPlainOut,
    })
        .loose()),
    otherwise: FunnelPlainOut.optional(),
})
    .loose();
/** Значение настройки в ответе: обычное, список или с условиями. */
// Вложенный union (а не плоский): в документации MCP вариант «обычное
// значение» остаётся прежним, новыми показаны список и вариант с правилами.
export const AdsFunnelSettingValueOut = z.union([
    FunnelScalarOut,
    FunnelListOut,
    AdsFunnelTargetedValueOut,
]);
/**
 * Значения настроек в ответе (без проверки длин — забота auto-ads). Значение
 * с правилами проходит как есть: иначе `soft(...)` обнулял бы весь блок.
 */
export const AdsFunnelSettingValuesOut = z.record(z.string(), AdsFunnelSettingValueOut);
/** Итоговые настройки воронки пачки (preview / пачка): значения + версия каталога проверки. */
export const AdsBatchFunnelSettingsOut = z.object({
    values: AdsFunnelSettingValuesOut,
    catalogVersion: opt(z.string()),
});
/** Настройки воронки шаблона в ответе; нет (старый auto-ads / не заданы) — null. */
export const AdsTemplateFunnelSettings = z.object({
    values: AdsFunnelSettingValuesOut,
    launchEditable: z.array(z.string()),
});
const templateInputShape = {
    name: z
        .string()
        .min(1, { message: 'Название обязательно' })
        .max(ADS_TEMPLATE_NAME_MAX, {
        message: `Название — не длиннее ${ADS_TEMPLATE_NAME_MAX} символов`,
    }),
    description: z
        .string()
        .max(ADS_TEMPLATE_DESCRIPTION_MAX, {
        message: `Описание — не длиннее ${ADS_TEMPLATE_DESCRIPTION_MAX} символов`,
    })
        .optional(),
    kind: AdsTemplateKind,
    /** Значения полей реестра по `key`; формат — по `type` поля. */
    values: ValuesRecord,
    /** Ключи полей, которые можно поменять при запуске. */
    launchEditable: z.array(z.string().min(1)),
    options: ValuesRecord,
    naming: AdsNamingInput,
    platformAccountIds: z.array(AdsId),
    /**
     * Настройки воронки шаблона (не уходят в Meta). PUT без поля — оставить
     * как есть; `null` или пустые `values` и `launchEditable` — убрать.
     */
    funnelSettings: AdsTemplateFunnelSettingsInput.nullable().optional(),
    /**
     * Группы шаблона (v4 §3.5): PUT без поля — оставить как есть, `null` —
     * убрать. Сохранить шаблон с группами можно только при включённых группах
     * auto-ads (`features.adSetGroups`), иначе 409 `adset-groups-disabled`.
     */
    adSetGroups: AdsAdSetGroupsInput.nullable().optional(),
};
export const AdsTemplateInput = z.object(templateInputShape).strict();
/** PUT: `expectedVersion` — версия, которую видел оператор (иначе 409 `template-version-conflict`). */
export const AdsTemplateUpdateRequest = z
    .object({ ...templateInputShape, expectedVersion: z.number().int().min(1) })
    .strict();
export const AdsNaming = z.object({
    prefix: z.string(),
    campaign: z.string(),
    adset: z.string(),
    ad: z.string(),
    /** Имя копии кампании (шаблон-копия); у обычных шаблонов нет. */
    campaignOverride: z.string().max(120).optional(),
});
export const AdsTemplate = z.object({
    id: RefId,
    name: z.string(),
    description: opt(z.string()),
    kind: AdsTemplateKind,
    values: ValuesRecord,
    launchEditable: z.array(z.string()),
    /** Ключи — всегда полные (`opt.copies`): короткие приводятся `normalizeAdsOptionKeys`. */
    options: ValuesRecord.transform(normalizeAdsOptionKeys),
    naming: AdsNaming,
    platformAccountIds: z.array(RefId),
    /**
     * Настройки воронки шаблона. Нет (старый auto-ads, не заданы) — null.
     * Без этого поля в DTO оно молча отрезалось бы на выходе admin-api.
     */
    funnelSettings: soft(AdsTemplateFunnelSettings),
    /** Группы шаблона (v4 §3.5); нет групп / старый auto-ads — null. */
    adSetGroups: v4(soft(AdsAdSetGroups)),
    /**
     * Шаблон-копия кампании (K2, `origin.kind = 'duplicate'`): одноразовый, скрыт из списка
     * шаблонов. Только для админки (модель его не получает). Обычный шаблон — null.
     */
    origin: v4AdminOnly(soft(z.object({
        kind: z.string(),
        campaignId: soft(z.string()),
        campaignName: soft(z.string()),
        platformAccountId: soft(z.string()),
    }))),
    version: z.number().int(),
    registryVersion: z.string(),
    archived: z.boolean(),
    summary: z.array(AdsFieldDisplay),
    /** Неизвестные/устаревшие поля относительно текущего реестра. */
    issues: z.array(AdsValueIssue),
    createdBy: opt(z.string()),
    updatedBy: opt(z.string()),
    createdAt: Iso,
    updatedAt: Iso,
});
export const AdsTemplateListQuery = z.object({
    includeArchived: z
        .enum(['0', '1'])
        .optional()
        .transform((v) => v === '1'),
    q: z.string().trim().max(ADS_TEMPLATE_NAME_MAX).optional(),
});
export const AdsTemplateListResponse = z.object({ items: z.array(AdsTemplate) });
export const AdsTemplateDuplicateRequest = z.object({ name: templateInputShape.name }).strict();
export const AdsFromCampaignRequest = z
    .object({
    platformAccountId: AdsId,
    campaignId: z.string().regex(/^\d+$/, { message: 'Id кампании — только цифры' }),
})
    .strict();
/** Группы черновика шаблона из кампании: `key` необязателен (решение 29.10 по ревью SB-1). */
export const AdsFromCampaignAdSetGroups = z.object({
    groups: z.array(z.object({
        key: z.string().optional(),
        label: z.string(),
        values: z.record(z.string(), z.unknown()),
    })),
});
export const AdsFromCampaignResponse = z.object({
    values: ValuesRecord,
    naming: opt(AdsNaming.partial()),
    /**
     * Опции шаблона из кампании (v4 §5.4: лесенка, копии, раскладка), ключи —
     * полные (`opt.copies`). Нет (старый auto-ads) — null.
     */
    options: v4(soft(ValuesRecord.transform(normalizeAdsOptionKeys))),
    /**
     * Группы шаблона из разных групп объявлений кампании (v4 §5.4) — черновик:
     * `key` нет (ключи выдаёт сервер при сохранении шаблона). Нет — null.
     */
    adSetGroups: v4(soft(AdsFromCampaignAdSetGroups)),
    warnings: z.array(AdsValueIssue),
});
/**
 * `POST /registry/naming-sample` — пример имён редактируемого шаблона: шаблоны
 * имён на условных значениях токенов, по тем же правилам, что у запуска
 * (`renderName` auto-ads). Считает только auto-ads — копии правил в UI нет.
 * Пределы — как у `naming` шаблона; передаются только непустые уровни.
 */
export const AdsNamingSampleRequest = z
    .object({
    prefix: z.string().max(20).optional(),
    kind: AdsTemplateKind,
    campaign: z.string().max(200).optional(),
    adset: z.string().max(200).optional(),
    ad: z.string().max(200).optional(),
})
    .strict();
export const AdsNamingSampleResponse = z.object({
    /** Готовые имена переданных уровней; уровня не было в запросе — null. */
    names: z.object({
        campaign: opt(z.string()),
        adset: opt(z.string()),
        ad: opt(z.string()),
    }),
    /** Неизвестные токены (`naming-unknown-token`) — под шаблоном своего уровня. */
    issues: z.array(AdsValueIssue),
});
// ---------------------------------------------------------------------------
// Пачки — /batches
// ---------------------------------------------------------------------------
export const ADS_BATCH_MAX_ACCOUNTS = 10;
export const ADS_BATCH_MAX_ITEMS = 50;
export const ADS_BATCH_MAX_COPIES = 10;
export const ADS_CREATIVE_MAX_VARIANTS = 7;
export const AdsCreativeVariant = z
    .object({
    primaryText: z.string().min(1).max(2200),
    headline: z.string().max(255).optional(),
    description: z.string().max(255).optional(),
})
    .strict();
/**
 * Откуда файл Meta: кабинет и ровно одно из `imageHash` / `videoId`. Приходит
 * из `GET /media/sources` и уходит в пачку как есть.
 */
export const AdsMediaOriginInput = z
    .object({
    platformAccountId: AdsId,
    imageHash: z.string().min(1).max(64).optional(),
    videoId: z
        .string()
        .regex(/^\d{1,30}$/, { message: 'Id видео — только цифры' })
        .optional(),
    name: z.string().max(255).optional(),
})
    .strict()
    .refine((o) => (o.imageHash === undefined) !== (o.videoId === undefined), {
    message: 'Нужно ровно одно: imageHash или videoId',
});
/** Креатив пачки: готовый пост (`pageId_postId`) или файл медиатеки + тексты. */
export const AdsCreativeItem = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('post'),
        objectStoryId: z.string().regex(/^\d+_\d+$/, { message: 'Пост — в формате pageId_postId' }),
    })
        .strict(),
    z
        .object({
        kind: z.literal('media'),
        /** `assetId` элемента выбора медиа (файл медиатеки или файл Meta + его `origin`). */
        assetId: AdsAssetId,
        /** Файл Meta, которого ещё нет в медиатеке («Уже в рекламе», «Библиотека кабинета») — как пришёл из выбора. */
        origin: AdsMediaOriginInput.optional(),
        variants: z.array(AdsCreativeVariant).min(1).max(ADS_CREATIVE_MAX_VARIANTS),
        cta: z.string().min(1).optional(),
        link: z
            .string()
            .url()
            .regex(/^https:\/\//, { message: 'Ссылка — только https' })
            .optional(),
    })
        .strict(),
]);
/** Часовой пояс ВСЕГО времени конструктора: ввод, «По нулям», час кабинета, показ. */
export const ADS_BUILDER_TIME_ZONE = 'America/Los_Angeles';
/**
 * Старт (всё — по Лос-Анджелесу): «Сразу» (через запас шаблона, 15 мин), «В
 * дату», «По нулям» (ближайшие 00:00), «Час кабинета» (ближайший час старта
 * из профиля кабинета).
 */
export const AdsStartInput = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('now') }).strict(),
    z.object({ mode: z.literal('at'), at: IsoIn }).strict(),
    z.object({ mode: z.literal('midnight') }).strict(),
    z.object({ mode: z.literal('account_hour') }).strict(),
]);
/** Направление шага ставки копий (v4 §3.3). */
export const AdsBidStepDirection = z.enum(['up', 'down']);
const AdsLadderBound = z
    .number()
    .int()
    .min(100, { message: 'Граница ставки — не меньше 1,00' })
    .max(100_000, { message: 'Граница ставки — не больше 1 000,00' });
const batchInputShape = {
    /** Ключ идемпотентности пачки: повтор с тем же ключом — 409 `idempotency-key-taken`. */
    idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, {
        message: 'Ключ идемпотентности — 8–64 символа: латиница, цифры, «_» и «-»',
    }),
    templateId: AdsId,
    templateVersion: z.number().int().min(1),
    platformAccountIds: z.array(AdsId).min(1).max(ADS_BATCH_MAX_ACCOUNTS),
    items: z.array(AdsCreativeItem).min(1).max(ADS_BATCH_MAX_ITEMS),
    /** Значения полей, изменённые при запуске (только `launchEditable` шаблона). */
    overrides: ValuesRecord,
    copies: z.number().int().min(1).max(ADS_BATCH_MAX_COPIES),
    layout: AdsBatchLayout,
    start: AdsStartInput,
    activation: AdsActivationMode,
    /** По умолчанию `execute`; `draft` — пачка создаётся без исполнения. */
    mode: AdsBatchMode.optional(),
    source: AdsBatchSource.optional(),
    /**
     * Настройки воронки этого запуска: только ключи `funnelSettings.launchEditable`
     * шаблона; `null` — убрать значение шаблона. Нет — как в шаблоне.
     */
    funnelSettings: AdsBatchFunnelSettingsInput.optional(),
    /**
     * Лесенка ставок по копиям (v4 §3.3), minor units: копия k (с 0) получает
     * ставку ± k × шаг, затем границу. Нет — опция шаблона (`opt.bidStep*`).
     */
    bidStepMinor: z
        .number()
        .int()
        .min(0, { message: 'Шаг ставки — не меньше 0' })
        .max(10_000, { message: 'Шаг ставки — не больше 100,00' })
        .optional(),
    bidStepDirection: AdsBidStepDirection.optional(),
    /** «Ставка не ниже» лесенки; `null` — убрать границу шаблона для этого запуска. */
    bidFloorMinor: AdsLadderBound.nullable().optional(),
    /** «Ставка не выше» лесенки; `null` — убрать границу шаблона для этого запуска. */
    bidCeilingMinor: AdsLadderBound.nullable().optional(),
    /** Группы шаблона этой пачки (v4 §3.5, [Р] 48): выбор по `keys` и правки по `edits`. */
    adSetGroups: AdsBatchAdSetGroupsInput.optional(),
};
export const AdsBatchInput = z.object(batchInputShape).strict();
/** Создание: `previewHash` из последнего preview (иначе 409 `preview-stale`). */
export const AdsBatchCreateRequest = z
    .object({ ...batchInputShape, previewHash: z.string().min(1) })
    .strict();
export const AdsPreviewCreative = z.object({
    kind: z.enum(['post', 'image', 'video']),
    objectStoryId: opt(z.string()),
    assetId: opt(z.string()),
    /**
     * Что за креатив — словами auto-ads (новый auto-ads, v4 D9): имя файла или
     * пост. Нет — UI подписывает сам по креативам экрана.
     */
    label: soft(z.string()),
    /** Имя файла (новый auto-ads). */
    name: soft(z.string()),
    /** Файл сначала переносится в кабинет: из медиатеки (`upload`) или из другого кабинета (`meta`). */
    transferFrom: soft(z.string()),
    /** Кабинет, из которого переносится файл (`transferFrom: 'meta'`). */
    fromPlatformAccountId: soft(z.string()),
    /**
     * «Куда ведёт» — ровно та ссылка, что уходит в Meta: ссылка креатива
     * (файл: креатив → шаблон → профиль кабинета; пост — его собственная) с
     * UTM шаблона. Считает компиляция auto-ads; null — ссылка неизвестна (пост
     * вне индекса кабинета) или старый auto-ads.
     */
    landing: soft(z.string()),
});
export const AdsPreviewAdSet = z.object({
    /** `s<j>` — localRef группы объявлений в спеке (v4 §3.5): стабильный ключ строки плана. */
    localRef: v4(soft(z.string())),
    name: z.string(),
    /** null — бюджет на уровне кампании (CBO). */
    dailyBudgetMinor: Minor.nullable(),
    /** Тот же бюджет ДО уникальных центов (ABO, v4 §3.11); CBO / старый auto-ads — null. */
    baseDailyBudgetMinor: v4(soft(Minor)),
    /** Ставка этой группы объявлений (лесенка, группа шаблона); нет ставки / старый — null. */
    bidAmountMinor: v4(soft(Minor)),
    /** Копия позиции, с 0 (v4 §3.3); старый auto-ads — null. */
    copyIndex: v4(soft(z.number().int())),
    /** Группа шаблона, из которой эта группа объявлений (`index` с 0, «①» = 0); без групп — null. */
    group: v4(soft(z.object({ key: z.string(), label: z.string(), index: z.number().int() }))),
    /** Поля, где значение группы шаблона отличается от общего (v4 §3.5); без групп — null. */
    differences: v4(soft(z.array(AdsFieldDisplay))),
    /** Advantage+ аудитория этой группы объявлений (v4 §3.6); старый auto-ads — null. */
    advantageAudience: v4(soft(z.boolean())),
    targeting: z.array(AdsFieldDisplay),
    /**
     * Языки группы с названиями из справочника языков (новый auto-ads, без
     * вызова Meta). Нет (старый auto-ads) — null: языки видны только строкой
     * `targeting` (id или пресет). `name: null` — id не нашёлся в справочнике.
     */
    locales: soft(z.array(z.object({ id: z.number().int(), name: z.string().nullable() }))),
    ads: z.array(z.object({ name: z.string(), creative: AdsPreviewCreative })),
});
/**
 * Файл, который запуск сначала перенесёт в свой кабинет (спека v3 §2
 * «автоперенос»): из медиатеки (`from: 'upload'`) или из другого кабинета
 * Meta (`'meta'`). `message` — готовая фраза auto-ads.
 */
export const AdsMediaTransfer = z.object({
    assetId: z.string(),
    kind: z.string(),
    name: opt(z.string()),
    from: z.string(),
    message: z.string(),
});
/** Сводный перенос пачки: тот же перенос + кабинет назначения. */
export const AdsBatchMediaTransfer = AdsMediaTransfer.extend({
    platformAccountId: z.string(),
    accountName: z.string(),
});
/** Список переносов: нет (старый auto-ads) или чужая форма — пусто, а не 502. */
const transfers = (item) => z
    .array(item)
    .nullish()
    .catch(null)
    .transform((v) => v ?? []);
export const AdsPreviewLaunch = z.object({
    index: z.number().int(),
    platformAccountId: RefId,
    accountName: z.string(),
    currency: z.string(),
    campaignName: z.string(),
    budgetLevel: z.enum(['CBO', 'ABO']),
    dailyBudgetMinor: Minor,
    /**
     * Бюджет кампании ДО уникальных центов (v4 §3.11): CBO — бюджет кампании,
     * ABO — сумма бюджетов групп объявлений до центов. Старый auto-ads — null.
     */
    baseDailyBudgetMinor: v4(soft(Minor)),
    /** null — стратегия не выбрана (в шаблоне её нет — план несёт ошибку `required-at-launch`). */
    bidStrategy: z.string().nullable(),
    /**
     * Ставка ВСЕХ групп объявлений кампании; null — ставки нет или у групп
     * объявлений разные ставки (лесенка, группы шаблона) — тогда см. `bidRange`.
     */
    bidAmountMinor: Minor.nullable(),
    /** Наименьшая / наибольшая ставка, когда они разные (v4 §3.5); одна или нет — null. */
    bidRange: v4(soft(z.object({ min: Minor, max: Minor }))),
    /**
     * Форма кампании (v4 §3.5): «1 кампания · 3 группы объявлений · 3
     * объявления (1-3-1)». Старый auto-ads — null.
     */
    structure: v4(soft(z.object({ campaigns: Count, adSets: Count, ads: Count, shape: z.string() }))),
    /** Порог «Мин. ROAS» числом (1.25) у стратегии ROAS — новый auto-ads; нет — UI берёт из формы. */
    roasFloor: soft(z.number()),
    /** Креативы кампании по порядку (новый auto-ads, v4 D9); нет — UI собирает из объявлений групп. */
    creatives: z.array(AdsPreviewCreative).nullish().catch(null),
    /**
     * «Куда ведёт» кампании: разные `creative.landing` её объявлений по порядку,
     * `null` в списке — объявление с неизвестной ссылкой. Нет поля (старый
     * auto-ads) — null: строка «Куда ведёт» не показывается.
     */
    landings: soft(z.array(z.string().nullable())),
    startTime: Iso.nullable(),
    /** Время старта для показа: «2026-10-14 03:00» в поясе `startTimeZone` (Лос-Анджелес). */
    startTimeLocal: z.string().nullable(),
    /** Пояс `startTimeLocal` — всегда Лос-Анджелес; старый auto-ads — null (тогда пояс кабинета). */
    startTimeZone: soft(z.string()),
    /** Пояс кабинета (справочно). */
    timezoneName: z.string(),
    copyIndex: z.number().int(),
    /** Когда запуск будет исполнен (лесенка по кабинетам). */
    executeAt: Iso,
    adSets: z.array(AdsPreviewAdSet),
    /** Файлы, которые запуск перенесёт в свой кабинет перед креативами. */
    mediaTransfers: transfers(AdsMediaTransfer),
    issues: z.array(AdsValueIssue),
});
export const AdsBatchTotals = z.object({
    campaigns: Count,
    adSets: Count,
    ads: Count,
    dailyBudgetMinorByCurrency: z.record(z.string(), Minor),
});
/**
 * Пункт чек-листа «Гарантии» плана. `text` — утверждение, `note` — пояснение
 * рядом (без повтора самого текста; null — показывать нечего). Оба считает
 * auto-ads; старый auto-ads их не присылает — null (экран показывает `label`).
 * `value` — сырое значение для старых клиентов.
 */
export const AdsPreviewGuarantee = z.object({
    key: z.string(),
    label: z.string(),
    text: soft(z.string()),
    value: z.string(),
    note: soft(z.string()),
});
// ---------------------------------------------------------------------------
// Деньги в день — считает ТОЛЬКО сервер (v4 §3.11; решение 29.10 по ревью SB-3)
//
// Одна функция auto-ads (`batchSpend`) для preview/create, маршрута расчёта
// до «Проверить», вида пачки и вида запуска. Клиент ничего не умножает:
// показывает `totalMinor` и формулу — структурно (`formula`/`breakdown`, тем же
// `formatMoney`, что везде) или готовой строкой `formulaRu` (запасной текст);
// `formulaEn` — для модели дословно. Валюта — валюта кабинета.
// ---------------------------------------------------------------------------
/** Одинаковые бюджеты ДО уникальных центов: «$80.00 × 6 = $480.00». */
export const AdsSpendBreakdownRow = z.object({
    baseUnitMinor: Minor,
    count: Count,
    subtotalMinor: Minor,
});
/**
 * Формула одной строкой («единица × число»): только когда ВСЕ бюджеты до
 * центов равны. `unitLabel` — подпись в нужной форме числа («групп
 * объявлений», «кампании»).
 */
export const AdsSpendFormula = z.object({
    unitMinor: Minor,
    count: Count,
    unitLabel: z.string(),
    currency: z.string(),
});
/** Итог в день по одной валюте. */
export const AdsSpendLine = z.object({
    currency: z.string().min(1),
    /**
     * false — это не настоящий итог в день (решение 29.10 по ревью AA-3): валюта
     * кабинета не в 1/100 (`currency-unsupported`) или пуст «Дневной бюджет»
     * (`budget-missing`). Тогда суммы НЕ показывать: `formula` null, `breakdown`
     * пуст, `formulaRu` / `formulaEn` говорят почему. Нет поля (AA-3 до ревью) — true.
     * auto-ads на непригодной строке отдаёт `totalMinor` / `totalBeforeCentsMinor`
     * = null (не число): это нормальный ответ, не 502.
     */
    usable: z.boolean().catch(true),
    /** Почему итога нет; строка — новое значение не роняет ответ. */
    unusableReason: z.string().nullable().catch(null),
    /**
     * = `totals.dailyBudgetMinorByCurrency[currency]` (с уникальными центами);
     * null — строка непригодна (`usable: false`), суммы нет. Нет поля — null.
     */
    totalMinor: opt(Minor),
    /** Та же сумма до уникальных центов; null — строка непригодна. */
    totalBeforeCentsMinor: opt(Minor),
    /** `totalMinor − totalBeforeCentsMinor` (0 — центы выключены; бывает < 0). */
    centsMinor: Minor.catch(0),
    level: z.enum(['CBO', 'ABO']),
    campaigns: Count,
    adSets: Count,
    accounts: Count,
    /** Сколько бюджетов складывается: кампаний (CBO) или групп объявлений (ABO). */
    units: Count,
    /** Одинаковые бюджеты до центов, по убыванию; пусто — план до v4 (оснований нет). */
    breakdown: z.array(AdsSpendBreakdownRow).catch([]),
    /** Формула одной строкой; null — бюджеты разные или план до v4. */
    formula: soft(AdsSpendFormula),
    /** Готовая формула / список по-русски — запасной текст админки. */
    formulaRu: z.string().catch(''),
    /** То же по-английски — для модели, дословно. */
    formulaEn: z.string().catch(''),
});
/**
 * «⚠ Это больше обычного» (v4 §3.11, без блокировки): `over-1000` — больше
 * $1 000 в день (только USD), `over-5x-median` — больше 5× медианы последних
 * пачек шаблона в той же валюте (`medianMinor`; меньше 3 пачек — null).
 */
export const AdsSpendUnusual = z.object({
    reason: z.enum(['over-1000', 'over-5x-median']),
    medianMinor: Minor.nullable(),
    currency: z.string(),
    /** Готовая строка для «Создать?» (RU); нет — UI пишет сам по `reason`. */
    messageRu: soft(z.string()),
    /** Готовая строка для модели (EN). */
    messageEn: soft(z.string()),
    /** Порог `over-1000` в minor, если сервер его пришлёт; нет — закон §3.11 (100 000 USD). */
    thresholdMinor: soft(Minor),
});
/** Деньги в день пачки / плана: по строке на валюту. */
export const AdsBatchSpend = z.object({
    lines: z.array(AdsSpendLine),
    /** Значение поля «Дневной бюджет» пачки; не задано — null. */
    budgetFieldMinor: opt(Minor),
    /** Итог до центов хоть в одной валюте ≠ полю «Дневной бюджет» (центы не в счёт). */
    differsFromField: z.boolean().catch(false),
    unusual: soft(AdsSpendUnusual),
    /** Все строки годятся и неизвестного нет — итог можно показывать как расход в день. */
    usable: z.boolean().catch(true),
    /**
     * Запуски, чьи деньги неизвестны и НЕ вошли в строки (нечитаемая запись
     * плана, кабинет без профиля): при > 0 итог неполный — UI говорит об этом.
     */
    unknownLaunches: Count.catch(0),
    /**
     * План не собран (компиляция не дала запусков из-за ошибок: `groups-all-removed`,
     * `too-many-adsets`…): денег нет совсем, `usable: false`, `lines: []`; тексты
     * объясняют без суммы. null / нет поля — собран.
     */
    notBuilt: soft(z.object({
        codes: z.array(z.string()).catch([]),
        messageRu: z.string().catch(''),
        messageEn: z.string().catch(''),
    })),
});
/** `POST /batches/spend` — тело как у preview (тот же ключ формы → те же центы). */
export const AdsBatchSpendRequest = AdsBatchInput;
/**
 * Ответ `POST /batches/spend` (v4 §3.11 п. 2): деньги и форма плана до
 * «Проверить» — без проверок постов и медиа, без `previewHash`, ничего не пишет.
 * `issues` — денежные замечания плана (`currency-unsupported`, пустой бюджет…).
 */
export const AdsBatchSpendResponse = z.object({
    spend: AdsBatchSpend,
    issues: z.array(AdsValueIssue).catch([]),
    structure: z.object({
        campaigns: Count,
        adSets: Count,
        /** ОЦЕНКА: медиа здесь не проверяются, каждый креатив считается доступным. */
        ads: Count,
        /** Наибольшее число групп объявлений в одной кампании плана (проверка «больше 50» в форме). */
        maxAdSetsPerCampaign: Count,
        /** Разные формы кампаний плана («1-3-1»), по порядку плана. */
        shapes: z.array(z.string()).catch([]),
    }),
});
export const AdsBatchPreview = z.object({
    previewHash: z.string(),
    registryVersion: z.string(),
    templateVersion: z.number().int(),
    issues: z.array(AdsValueIssue),
    /**
     * Все проблемы плана — пачки и запусков — сведённые auto-ads в одну запись
     * на проблему (`launchIndexes`, первым — `launchIndex`). Есть — список
     * плана строится по нему; нет (старый auto-ads) — UI сводит сам.
     */
    allIssues: z.array(AdsValueIssue).nullish().catch(null),
    launches: z.array(AdsPreviewLaunch),
    /** Все переносы файлов пачки по кабинетам («Файл «X» будет перенесён в кабинет «Y»»). */
    mediaTransfers: transfers(AdsBatchMediaTransfer),
    totals: AdsBatchTotals,
    guarantees: z.array(AdsPreviewGuarantee),
    /** Нет ни одной проблемы с `severity: 'error'`. */
    canCreate: z.boolean(),
    /**
     * Время старта плана пересчитают при создании («Сразу» — момент создания +
     * запас), `startNote` — готовая фраза для подтверждения (новый auto-ads, v4
     * D17). Нет (старый auto-ads) — null.
     */
    startRecomputedAtCreate: soft(z.boolean()),
    startNote: soft(z.string()),
    /**
     * Итоговые настройки воронки запусков пачки (шаблон + переопределения) и
     * версия каталога, по которой их проверили. Нет (старый auto-ads, настроек
     * нет) — null.
     */
    funnelSettings: soft(AdsBatchFunnelSettingsOut),
    /**
     * Деньги в день плана (v4 §3.11) — рядом с `totals`, тот же итог. Нет
     * (старый auto-ads) — null: UI показывает прежний итог из `totals`.
     */
    spend: v4(soft(AdsBatchSpend)),
});
/** Созданные объекты Meta по localRef. */
export const AdsLaunchCreated = z.object({
    campaignId: opt(z.string()),
    adSetIds: z.record(z.string(), z.string()).default({}),
    adIds: z.record(z.string(), z.string()).default({}),
});
export const AdsLaunchActivation = z.object({
    mode: AdsActivationMode,
    status: AdsActivationStatus,
    at: opt(Iso),
    /**
     * Почему поставлен статус — машинный код auto-ads (`late`, `cancelled`,
     * `drift`, `verify-unavailable`, `pause-failed`, …); строка, а не enum:
     * новый код не роняет ответ.
     */
    reason: opt(z.string()),
    /**
     * `pause_failed`: сколько раз auto-ads уже пытался поставить кампанию на
     * паузу по текущему поводу (после предела — `needs_attention`,
     * `reason: 'pause-failed'`). Старый auto-ads — null.
     */
    attempts: opt(Count),
});
/**
 * Пауза, поставленная конструктором: человеком (`POST /launches/:id/pause`,
 * `source: 'user'`) или системой при отмене пачки до старта (`source:
 * 'system'`, `reason: 'batch-cancelled'`). Старый auto-ads — без `source` и
 * `reason` (читается как пауза человека). Мягко: чужая форма — null, а не 502.
 */
export const AdsManualPause = z.object({
    at: Iso,
    by: opt(z.string()),
    source: soft(z.string()),
    reason: soft(z.string()),
});
const manualPauseField = AdsManualPause.nullish()
    .catch(null)
    .transform((v) => v ?? null);
/**
 * Кто и почему поставил запуск на паузу — готовыми фактами auto-ads (новый
 * auto-ads): `source` `user` / `system`, `reason` `manual` / `batch-cancelled`,
 * `message` — готовая русская фраза. Нет (старый auto-ads) — UI выводит из
 * `manualPause`.
 */
export const AdsPauseInfo = z.object({
    at: Iso,
    source: z.string(),
    reason: z.string(),
    message: opt(z.string()),
    by: opt(z.string()),
});
const pauseInfoField = soft(AdsPauseInfo);
/**
 * Как auto-ads решил создавать объекты запуска (`opt.createMode`): `ACTIVE` —
 * «как в Ads Manager», включёнными с датой старта; `PAUSED` — на паузе.
 * `reason` — почему на паузе, хотя просили включёнными (`start-too-close`,
 * `manual-activation`, …); `downgraded` — решение откатили на ходу (до старта
 * осталось меньше 2 минут). Строки: новое значение не роняет ответ.
 */
export const AdsCreateDecision = z.object({
    mode: z.string(),
    status: z.string(),
    at: opt(Iso),
    reason: soft(z.string()),
    downgraded: soft(z.object({ at: Iso, reason: z.string(), pausedAt: opt(Iso) })),
});
const createDecisionField = AdsCreateDecision.nullish()
    .catch(null)
    .transform((v) => v ?? null);
/**
 * Meta отклонила запуск на предпроверке (validate_only, auto-ads `remoteValidation`):
 * ничего не создано, шагов нет. `text` — готовая строка первой ошибки («Meta
 * отклонила при проверке: <title> — <userMsg>»), `items` — все упавшие проверки
 * по порядку. Новый auto-ads (#917); нет или чужая форма — null, блок не
 * показывается.
 */
export const AdsRemoteValidationFailure = z.object({
    at: soft(Iso),
    items: z
        .array(z.object({
        /** `creative` | `campaign` | `media.image` | `media.video` — строка, новое не роняет. */
        kind: z.string(),
        /** `s0/a1v0`, `campaign` или localRef файла. */
        localRef: z.string(),
        code: z.union([z.string(), z.number()]).transform(String),
        subcode: soft(z.number()),
        title: soft(z.string()),
        userMsg: soft(z.string()),
        message: soft(z.string()),
        fbtraceId: soft(z.string()),
    }))
        .catch([]),
    text: z.string().min(1),
});
const remoteValidationFailureField = soft(AdsRemoteValidationFailure);
/**
 * Запуск опоздал к старту, пока ждал включения (v4 §8.1): все числа считает
 * auto-ads (у UI и модели нет надёжных часов). `reason` — `late` / `manual` /
 * `start-too-close` (строка: новое значение не роняет ответ). `verifyFresh` —
 * сверка не старше `verifyMaxAgeMinutes`, иначе «Перепроверить и включить».
 */
export const AdsLaunchLateStart = z.object({
    startTime: Iso,
    lateByMinutes: Count,
    reason: z.string(),
    canActivateLate: z.boolean(),
    needsForce: z.boolean(),
    verifiedAt: Iso.nullable(),
    verifiedAgoMinutes: Count.nullable(),
    verifyFresh: z.boolean(),
    verifyMaxAgeMinutes: Count,
    waitingOver24h: z.boolean(),
});
/** Группа шаблона группы объявлений запуска (v4 §3.5, место 10): `localRef` → группа и копия. */
export const AdsLaunchAdSetGroupRef = z.object({
    localRef: z.string(),
    groupKey: z.string(),
    label: z.string(),
    copyIndex: z.number().int(),
    /**
     * Чем группа отличалась от общего В ЭТОМ ЗАПУСКЕ — снимок плана пачки
     * (`adSets[].differences` превью, с которым создан запуск; E-2 P2-4):
     * карточка запуска показывает отличия по нему, а не по текущему шаблону,
     * который могли поменять после запуска. Нет (старый auto-ads) — null/нет.
     */
    differences: v4AdminOnly(soft(z.array(AdsFieldDisplay)).optional()),
    /**
     * Номер группы в шаблоне («①» = 0), замороженный пачкой (ревью E-2, P2): тот же,
     * что в колонке «Группы шаблона» пачки. Нет (старый auto-ads) — null/нет.
     */
    index: v4(soft(z.number().int().min(0)).optional()),
});
/** Поля v4 карточки и строки запуска: все мягкие — старый auto-ads их не шлёт (null). */
const launchV4Fields = {
    /** Группы шаблона его групп объявлений (v4 §3.5); без групп — null. */
    adSetGroups: v4(soft(z.array(AdsLaunchAdSetGroupRef))),
    /**
     * Шаблон и его версия, по которым собран запуск (`meta_launches.templateRef`):
     * карточка запуска показывает отличия групп, только если шаблон с тех пор не
     * менялся. Нет / старый auto-ads — null.
     */
    templateRef: v4(soft(z.object({ id: RefId, version: z.number().int() }))),
    /** Опоздал к старту, ждёт включения (v4 §8.1); не опоздал / старый auto-ads — null. */
    lateStart: v4(soft(AdsLaunchLateStart)),
    /** Деньги в день этого запуска (v4 §3.11 п. 3); неизвестно / старый auto-ads — null. */
    spend: v4(soft(AdsSpendLine)),
};
export const AdsLaunchBrief = z.object({
    id: RefId,
    batchId: opt(RefId),
    platformAccountId: RefId,
    adAccountId: z.string(),
    /** Внутреннее состояние оркестратора auto-ads; для показа — `displayStatus`. */
    state: z.string(),
    displayStatus: AdsLaunchDisplayStatusIn,
    campaignName: opt(z.string()),
    dailyBudgetMinor: opt(Minor),
    /** Имя кабинета (новый auto-ads, аудит MCP I3); нет / неизвестно — null. */
    adAccountName: soft(z.string()),
    /** Валюта кабинета (новый auto-ads); нет — UI берёт валюту из списка кабинетов. */
    currency: soft(z.string()),
    startTime: opt(Iso),
    /** Старт для показа («2026-10-14 03:00») в поясе `startTimeZone`. */
    startTimeLocal: soft(z.string()),
    /** Пояс `startTimeLocal` — Лос-Анджелес (новый auto-ads); нет — старый, пояс кабинета. */
    startTimeZone: soft(z.string()),
    /** Пояс кабинета (справочно), новый auto-ads; нет — из списка кабинетов. */
    timezoneName: soft(z.string()),
    created: AdsLaunchCreated,
    verify: opt(z.object({
        at: Iso,
        hasErrors: z.boolean(),
        errors: Count,
        warnings: Count,
        /**
         * Последняя сверка после включения — та же запись, по которой auto-ads
         * выводит `displayStatus`. Счётчики, повод и «не удалось проверить» —
         * новый auto-ads (мягко: нет или чужая форма — null).
         */
        postActivate: opt(z.object({
            at: Iso,
            hasErrors: z.boolean(),
            errors: soft(Count),
            warnings: soft(Count),
            trigger: soft(z.string()),
            cleared: soft(z.string()),
            /** Саму сверку провести не удалось (Meta не ответила) — ничего не сравнили. */
            unavailable: soft(z.boolean()),
        })),
    })),
    activation: opt(AdsLaunchActivation),
    createDecision: createDecisionField,
    plannedExecuteAt: opt(Iso),
    progress: z.object({ done: Count, total: Count }),
    /**
     * Ошибка шага; иначе ошибка задачи; иначе (ничего не создано) — первая
     * ошибка предпроверки Meta (код `remote-validation`, новый auto-ads).
     */
    lastError: opt(AdsErrorInfo),
    remoteValidationFailure: remoteValidationFailureField,
    adsManagerUrl: opt(z.string()),
    /**
     * Какие действия над запуском сервер разрешает сейчас (`pause`, `rollback`,
     * …) — новый auto-ads. Есть — UI показывает кнопки только по нему (и по
     * правам); нет (старый auto-ads) — выводит сам по статусу.
     */
    availableActions: z.array(z.string()).nullish().catch(null),
    manualPause: manualPauseField,
    pauseInfo: pauseInfoField,
    /** ISO начала показа (граница конструктора = старт показа); нет — не начался / старый auto-ads. */
    deliveryStartedAt: z.string().nullish().catch(null),
    /** `started` — показ начался, запуск кампанию больше не ведёт; нет — старый auto-ads. */
    phase: z.enum(['pre-start', 'started']).nullish().catch(null),
    /** Пачку отменили до создания запуска (новый auto-ads; статус — `cancelled`). */
    cancelled: soft(z.boolean()),
    /**
     * Подписи групп шаблона, из которых собраны группы объявлений запуска (E-1
     * D10, колонка «Группы шаблона» страницы пачки): `GET /batches/:id` отдаёт
     * их в строке запуска (там нет `adSetGroups`). Без групп / старый auto-ads — null.
     */
    groupLabels: v4(soft(z.array(z.string()))),
    /**
     * Номера групп `groupLabels` в шаблоне («①» = 0), по порядку `groupLabels` —
     * замороженные пачкой (ревью E-2, P2): правка шаблона нумерацию не сдвигает.
     * Нет / старый auto-ads — null (номер не показывается, а не угадывается).
     */
    groupIndexes: v4(soft(z.array(z.number().int().min(0)))),
    ...launchV4Fields,
    createdBy: opt(z.string()),
    createdAt: Iso,
    updatedAt: Iso,
});
/**
 * Счётчики запусков пачки по `displayStatus` + `total`. Отсутствующий
 * статус — 0. Ключи обязаны совпадать с `ADS_LAUNCH_DISPLAY_STATUSES`
 * (держит тест).
 */
export const AdsBatchCounts = z.object({
    draft: Count.default(0),
    scheduled: Count.default(0),
    creating: Count.default(0),
    ready_paused: Count.default(0),
    blocked: Count.default(0),
    activating: Count.default(0),
    waiting_start: Count.default(0),
    running: Count.default(0),
    cancelled: Count.default(0),
    paused: Count.default(0),
    paused_on_drift: Count.default(0),
    pause_failed: Count.default(0),
    needs_attention: Count.default(0),
    failed: Count.default(0),
    rolling_back: Count.default(0),
    rolled_back: Count.default(0),
    total: Count,
});
export const AdsBatch = z.object({
    id: RefId,
    idempotencyKey: z.string(),
    templateId: RefId,
    templateName: z.string(),
    templateVersion: z.number().int(),
    platformAccountIds: z.array(RefId),
    /** Кабинеты пачки с именами (порядок `platformAccountIds`), новый auto-ads (аудит MCP I3); нет — null. */
    accounts: soft(z.array(z.object({ platformAccountId: RefId, adAccountName: z.string().nullable() }))),
    state: AdsBatchStateIn,
    activation: AdsActivationMode,
    mode: AdsBatchMode,
    source: AdsBatchSource,
    counts: AdsBatchCounts,
    totals: AdsBatchTotals,
    /** Только в `GET /batches/:id`; в списке — null. */
    launches: opt(z.array(AdsLaunchBrief)),
    /** Что auto-ads поменял сам после preview (например, `schedule-recomputed`). */
    warnings: z
        .array(z.object({ code: z.string(), message: z.string(), at: opt(Iso) }))
        .nullable()
        .default(null),
    /**
     * Итоговые настройки воронки, с которыми создана пачка (их получает каждый
     * запуск), и версия каталога проверки. Нет (старый auto-ads, настроек нет) — null.
     */
    funnelSettings: soft(AdsBatchFunnelSettingsOut),
    /**
     * Структура пачки (v4 §3.5, колонка «Структура»): формы кампаний («1-2-1»,
     * уникальные) и названия выбранных групп шаблона. Пачка до v4 — null.
     */
    structure: v4(soft(z.object({
        shapes: z.array(z.string()),
        // названия групп — отдельной пометкой: MCP открывает `structure` раньше групп (M-4)
        groupLabels: v4(z.array(z.string())),
        /** Номера `groupLabels` в шаблоне («①» = 0, ревью E-2 P2); нет — null. */
        groupIndexes: v4(soft(z.array(z.number().int().min(0))).optional()),
    }))),
    /** Деньги в день по сохранённому плану (v4 §3.11 п. 4) — для «Выполнить»; нет — null. */
    spend: v4(soft(AdsBatchSpend)),
    /**
     * Опоздавшие запуски, ждущие включения (v4 §8.3, «Включить все сейчас…»):
     * `activatable` — можно включить прогоном, `excluded` — нельзя (`verify-errors`
     * — только по одному с `force`; `busy` — идёт действие), `spendByCurrency` —
     * сумма в день включаемых, `unknownSpendLaunches` — включаемые без известной
     * суммы (UI: «сумма неизвестна для N»). Нет опоздавших / старый — null.
     */
    lateStart: v4(soft(z.object({
        launches: Count,
        activatable: z.array(RefId),
        excluded: z.array(z.object({ launchId: RefId, reason: z.string() })),
        spendByCurrency: z.record(z.string(), Minor),
        unknownSpendLaunches: Count.catch(0),
    }))),
    /**
     * Ближайший плановый старт запусков пачки, ждущих ручного включения и ещё не
     * опоздавших (ревью E-2 P3): список пачек обновляется сам, только пока такой
     * старт близко. Нет таких / старый auto-ads — null.
     */
    manualStartNextAt: v4(soft(Iso)),
    /**
     * Запуски, созданные не полностью (v4 §5.3): `failed`, кампания есть, групп
     * объявлений / объявлений меньше плана, ничего не откатано — по группе
     * шаблона и кабинету. Нет / старый auto-ads — null.
     */
    incomplete: v4(soft(z.object({
        launches: Count,
        /**
         * Точный список недосозданных запусков (AA-RC): откат «неполных» — по нему,
         * а не по счётчику. Старый auto-ads — null (UI откатывает как раньше).
         */
        launchIds: soft(z.array(RefId)),
        // по группам шаблона — отдельной пометкой: MCP открывает `incomplete` раньше групп (M-4)
        reasonsByGroup: v4(z.array(z.object({
            groupKey: z.string().nullable(),
            label: z.string().nullable(),
            accountName: z.string().nullable(),
            count: Count,
        }))),
    }))),
    /**
     * Шаг лесенки ставок, с которым создана пачка (AA-RC): `source` — из шаблона
     * или из входа пачки. Неизвестный `source` — null, а не 502. Нет — null.
     */
    bidStep: v4(soft(z.object({
        bidStepMinor: z.number().int().nonnegative(),
        source: z.enum(['template', 'batch']).nullable().catch(null),
    }))),
    createdBy: opt(z.string()),
    createdAt: Iso,
    updatedAt: Iso,
});
export const ADS_BATCH_LIST_MAX_LIMIT = 100;
export const AdsBatchListQuery = z.object({
    state: AdsBatchState.optional(),
    platformAccountId: AdsId.optional(),
    source: AdsBatchSource.optional(),
    limit: z.coerce.number().int().min(1).max(ADS_BATCH_LIST_MAX_LIMIT).default(20),
    cursor: z.string().min(1).optional(),
});
export const AdsBatchListResponse = cursorPage(AdsBatch).extend({
    /**
     * Фильтр `state` вычисляемый: auto-ads просмотрел ограниченное число пачек и
     * остановился — страница может быть пустой при `nextCursor` (аудит MCP L10).
     * Новый auto-ads; нет — null.
     */
    scanIncomplete: soft(z.boolean()),
});
export const ADS_BATCH_EXECUTE_MAX_LAUNCHES = 100;
/**
 * `POST /batches/:id/execute`. Пустое тело: черновик — выполнить целиком;
 * пачка в режиме исполнения — возобновить (перепланировать отданные назад,
 * повторить упавшие, поднять зависшие и потерянные в очереди; нечего — 409
 * `invalid-state`). `launchIds` — только эти запуски (только у пачки в
 * режиме исполнения; у черновика — 409 `invalid-state`).
 */
export const AdsBatchExecuteRequest = z
    .object({ launchIds: z.array(AdsId).min(1).max(ADS_BATCH_EXECUTE_MAX_LAUNCHES).optional() })
    .strict();
/** `rollbackReady` — заодно откатить уже созданные и не включённые запуски. */
export const AdsBatchCancelRequest = z.object({ rollbackReady: z.boolean().optional() }).strict();
/**
 * Ответ отмены пачки (аудит MCP I5, новый auto-ads): пачка + что отмена НЕ
 * остановила. `stillSpending` — запуски, уже стартовавшие/включённые (отмена
 * их не ставит на паузу, они продолжают тратить); `pauseFailed` — id
 * запусков, чью паузу до старта сделать не удалось (они начнут тратить в
 * момент старта). HTTP 200 — сигнал только в этих полях. Нет — null.
 */
export const AdsBatchCancelResponse = AdsBatch.extend({
    stillSpending: soft(z.array(z.looseObject({ launchId: RefId }))),
    pauseFailed: soft(z.array(RefId)),
    /** Запуски пачки, уже начавшие показ: отмена их не останавливает, кампании живут во вкладке «Кампании». */
    startedLaunchIds: z.array(RefId).catch([]),
});
// ---------------------------------------------------------------------------
// «Создать такую же» — GET /batches/:id/repeat-input (v4 §7)
// ---------------------------------------------------------------------------
/** Query: повтор одного запуска пачки (`launchId` — из этой пачки, иначе 400 `launch-not-in-batch`). */
export const AdsBatchRepeatInputQuery = z
    .object({
    launchId: z
        .string()
        .regex(/^[a-f0-9]{24}$/, { message: 'Неверный идентификатор запуска' })
        .optional(),
})
    .strict();
/**
 * Готовый вход preview из старой пачки — СТРОГИЙ разбор (RM В6 п. 4): лишний
 * ключ = «auto-ads прислал не то» (502), а не тихая потеря настройки. Без
 * `idempotencyKey` и `source` (UI создаёт новый ключ); `start: null` — старт
 * надо выбрать заново; кабинетов и креативов может не остаться (заметки
 * `no-accounts` / `no-items` объясняют почему) — форма откроется пустой.
 */
export const AdsBatchRepeatInputBody = AdsBatchInput.omit({ idempotencyKey: true, source: true })
    .extend({
    // как у auto-ads (`BatchInputRaw`): нет — опция шаблона / пусто (решение 29.10 по ревью SB-1)
    copies: AdsBatchInput.shape.copies.optional(),
    layout: AdsBatchInput.shape.layout.optional(),
    overrides: AdsBatchInput.shape.overrides.optional(),
    platformAccountIds: z.array(AdsId).max(ADS_BATCH_MAX_ACCOUNTS),
    items: z.array(AdsCreativeItem).max(ADS_BATCH_MAX_ITEMS),
    start: AdsStartInput.nullable(),
})
    .strict();
/**
 * Заметка повтора: что не перенеслось и почему (`message` — RU для UI, с
 * названиями вместо id; `params` — числа и версии для EN-текста MCP).
 * Код — строка: новый код не роняет ответ.
 */
export const AdsRepeatNote = z.object({
    code: z.string().min(1),
    severity: AdsIssueSeverity,
    message: z.string(),
    fieldKey: soft(z.string()),
    itemIndex: soft(z.number().int()),
    groupKey: soft(z.string()),
    params: soft(z.record(z.string(), z.union([z.string(), z.number()]))),
    /** Только у `template-changed`: до 5 строк «поле: было → стало». */
    changedFields: soft(z.array(z.object({
        label: z.string(),
        before: z.string(),
        after: z.string(),
        /** Для модели (MCP M-3): ключ поля реестра и значения по-английски; нет — null. */
        labelEn: soft(z.string()),
        beforeEn: soft(z.string()),
        afterEn: soft(z.string()),
    }))),
});
/**
 * Ответ «Создать такую же» (v4 §7.2): вход старой пачки, сверенный с ТЕКУЩИМ
 * шаблоном, деньги «было → станет» (`spend`, показывать до «Проверить») и
 * заметки. Ничего не пишет; `previewHash` и ключа идемпотентности нет — дальше
 * обычный preview → create.
 */
export const AdsBatchRepeatInput = z.object({
    source: z.object({
        batchId: RefId,
        launchId: RefId.nullable(),
        createdAt: Iso,
        templateName: z.string(),
        templateVersionThen: z.number().int(),
        /** Режим старой пачки (черновик / исполнение) — переносится как был. */
        mode: AdsBatchMode,
        activation: AdsActivationMode,
    }),
    /** ТЕКУЩИЙ шаблон; null — удалён. */
    template: z
        .object({
        id: RefId,
        name: z.string(),
        version: z.number().int(),
        archived: z.boolean(),
    })
        .nullable(),
    /** null — пачку не удалось прочитать (заметка `input-unreadable`). */
    input: AdsBatchRepeatInputBody.nullable(),
    /** Группы текущего шаблона, которых не было в той пачке (без галочки, бейдж «новая»). */
    newGroupKeys: z.array(z.string()).catch([]),
    spend: z.object({
        before: z.array(AdsSpendLine),
        /** null — входа нет. */
        after: z.array(AdsSpendLine).nullable(),
    }),
    notes: z.array(AdsRepeatNote),
});
// ---------------------------------------------------------------------------
// Запуски — /launches/:id
// ---------------------------------------------------------------------------
/** Расхождение сверки «что создали ↔ что прочитали из Meta». */
export const AdsDiscrepancy = z.object({
    kind: z.string(),
    localRef: z.string(),
    metaId: opt(z.string()),
    path: z.string(),
    /** null — путь не сопоставлен полю реестра. */
    fieldKey: z.string().nullable().default(null),
    /**
     * RU-подпись поля (или путь, если поля нет). Пустая строка — сверка
     * записана до подписей (старый запуск): UI показывает `path`.
     */
    label: z.string().default(''),
    expected: z.unknown(),
    actual: z.unknown(),
    severity: AdsSeverity,
    /**
     * Ключи гарантий реестра, которые нарушает расхождение — сопоставляет
     * auto-ads (пути гарантий наружу не отдаются). Нет (старый auto-ads) — [].
     */
    guaranteeKeys: z
        .array(z.string())
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
});
/**
 * Заметка сверки (auto-ads `VerifyNote`, решение 160): ФАКТ, а не расхождение —
 * Meta сама так делает (например, включает расширение LAL после старта у всех
 * групп). Форма как у расхождения, `severity: 'info'`; на ошибки, паузу,
 * гарантии и кнопки не влияет. `label` — текст заметки (RU из auto-ads).
 */
export const AdsVerifyNote = z.object({
    kind: z.string(),
    localRef: z.string(),
    metaId: opt(z.string()),
    path: z.string(),
    fieldKey: z.string().nullable().default(null),
    label: z.string().default(''),
    expected: z.unknown(),
    actual: z.unknown(),
    severity: z.literal('info').catch('info'),
});
/**
 * Заметки сверки — МЯГКО: старый auto-ads их не отдаёт, кривая форма не
 * должна ронять ответ запуска — тогда [].
 */
const verifyNotes = () => z
    .array(AdsVerifyNote)
    .nullish()
    .catch(null)
    .transform((v) => v ?? []);
/** Вердикт гарантии запуска: соблюдена / нарушена / сверки ещё не было / не сверяется. */
export const ADS_GUARANTEE_STATES = ['ok', 'broken', 'pending', 'by-design'];
export const AdsGuaranteeState = z.enum(ADS_GUARANTEE_STATES);
/**
 * Гарантия запуска с вердиктом по сверкам (`GET /launches/:id` → `guarantees`),
 * считает auto-ads. Состояние — строка: новое значение не роняет ответ, экран
 * показывает его как есть. `related` — расхождения, которые её нарушили.
 */
export const AdsLaunchGuarantee = z.object({
    key: z.string(),
    label: z.string(),
    text: z.string(),
    detail: z.string(),
    state: z.union([AdsGuaranteeState, z.string().min(1)]),
    related: z.array(AdsDiscrepancy).default([]),
    /**
     * Предупреждения по путям гарантии из текущей сверки: показываются, но
     * гарантию не нарушают. Мягкое поле: старый auto-ads его не отдаёт — [].
     */
    warnings: z
        .array(AdsDiscrepancy)
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
});
/**
 * Статусы шага запуска — как в auto-ads (`orchestrator/launch-state.ts`,
 * `LAUNCH_STEP_STATUSES`). Поле `status` остаётся строкой (новое значение не
 * роняет ответ), экраны сверяются с этим списком, а не угадывают.
 */
export const ADS_LAUNCH_STEP_STATUSES = [
    'pending',
    'in_progress',
    'ambiguous',
    'done',
    'failed',
    'skipped',
    'rolled_back',
];
export const AdsLaunchStep = z.object({
    seq: z.number().int(),
    kind: z.string(),
    localRef: z.string(),
    status: z.string(),
    metaId: opt(z.string()),
    attempts: Count,
    startedAt: opt(Iso),
    finishedAt: opt(Iso),
    error: opt(AdsErrorInfo),
    /**
     * Пояснение к статусу шага (новый auto-ads): `already-uploaded` — файл уже
     * лежит в кабинете, шаг загрузки `skipped` не нужен. Строка: новое значение
     * не роняет ответ.
     */
    note: soft(z.string()),
});
/** Локальная проверка правил до отправки в Meta. */
export const AdsRuleIssue = z.object({
    severity: AdsSeverity,
    code: z.string(),
    path: z.array(z.union([z.string(), z.number()])),
    message: z.string(),
});
export const AdsLaunchStateHistoryEntry = z.object({
    from: z.string().nullable(),
    to: z.string(),
    at: Iso,
    reason: opt(z.string()),
});
/**
 * Одна сверка после включения. `error` — сама сверка не удалась (ничего не
 * сравнили; `hasErrors` тогда true); `trigger` — что её запустило
 * (`activate`, `prestart`, `postactivate`, `retry-N`, `manual`, …).
 */
export const AdsPostActivateVerify = z.object({
    at: Iso,
    hasErrors: z.boolean(),
    discrepancies: z.array(AdsDiscrepancy).default([]),
    /** Заметки Meta этой сверки (факты, не расхождения). Старый auto-ads — []. */
    notes: verifyNotes(),
    error: opt(AdsErrorInfo),
    trigger: opt(z.string()),
    /**
     * Эта чистая сверка сняла `activation.status = needs_attention` (запуск
     * вернулся в норму) — `'needs_attention'`. Строка, а не литерал: новое
     * значение не роняет ответ.
     */
    cleared: opt(z.string()),
});
export const AdsLaunchVerify = z.object({
    at: Iso,
    hasErrors: z.boolean(),
    discrepancies: z.array(AdsDiscrepancy),
    /** Заметки Meta сверки при создании (факты, не расхождения). Старый auto-ads — []. */
    notes: verifyNotes(),
    /** Последняя сверка после включения (дрейф → пауза или «требует внимания»). */
    postActivate: opt(AdsPostActivateVerify),
    /** Все сверки после включения, старые первыми (и плановые пересверки). Старый auto-ads — []. */
    postActivateHistory: z
        .array(AdsPostActivateVerify)
        .nullish()
        .transform((v) => v ?? []),
});
/** Подмножество `MetaLaunchView` auto-ads, нужное экрану «Запуск». */
export const AdsLaunchDetail = z.object({
    id: RefId,
    batchId: opt(RefId),
    /** Кабинет запуска — для пояса и валюты, если их нет в ответе. */
    platformAccountId: soft(z.string()),
    /**
     * Сводка запуска (новый auto-ads, аудит MCP I2): имя кампании, ПЛАНОВЫЙ
     * дневной бюджет в минорных единицах (центах) валюты кабинета, кабинет Meta
     * и его имя. Нет — null (старый auto-ads; тогда — из `compiled`).
     */
    campaignName: soft(z.string()),
    dailyBudgetMinor: soft(Minor),
    adAccountId: soft(z.string()),
    adAccountName: soft(z.string()),
    currency: soft(z.string()),
    /** Старт запуска (ISO) и он же для показа по Лос-Анджелесу — новый auto-ads. */
    startTime: soft(Iso),
    startTimeLocal: soft(z.string()),
    startTimeZone: soft(z.string()),
    timezoneName: soft(z.string()),
    state: z.string(),
    displayStatus: AdsLaunchDisplayStatusIn,
    activation: opt(AdsLaunchActivation),
    createDecision: createDecisionField,
    steps: z.array(AdsLaunchStep),
    created: AdsLaunchCreated,
    verify: opt(AdsLaunchVerify),
    localIssues: z.array(AdsRuleIssue).default([]),
    /** Отказ предпроверки Meta (шагов нет) — причина «Ошибки» запуска. */
    remoteValidationFailure: remoteValidationFailureField,
    stateHistory: z.array(AdsLaunchStateHistoryEntry).default([]),
    /** Скомпилированное дерево объектов Meta — показывается как есть (JSON). */
    compiled: z.unknown(),
    adsManagerUrl: opt(z.string()),
    /**
     * Какие действия над запуском сервер разрешает сейчас (`pause`, `rollback`,
     * …) — новый auto-ads. Есть — UI показывает кнопки только по нему (и по
     * правам); нет (старый auto-ads) — выводит сам по статусу.
     */
    availableActions: z.array(z.string()).nullish().catch(null),
    manualPause: manualPauseField,
    pauseInfo: pauseInfoField,
    /** ISO начала показа (граница конструктора = старт показа); нет — не начался / старый auto-ads. */
    deliveryStartedAt: z.string().nullish().catch(null),
    /** `started` — показ начался, запуск кампанию больше не ведёт; нет — старый auto-ads. */
    phase: z.enum(['pre-start', 'started']).nullish().catch(null),
    cancelled: soft(z.boolean()),
    /**
     * Все гарантии реестра с вердиктом по сверкам запуска — считает auto-ads.
     * Нет (старый auto-ads) — null: блок «Гарантии» не показывается.
     */
    guarantees: soft(z.array(AdsLaunchGuarantee)),
    /**
     * Настройки воронки запуска: значения, документ настроек и номер версии
     * (план K6). Нет (старый auto-ads, у запуска их нет) — null. Только чтение
     * (решение Р5): изменить настройки запуска нельзя.
     */
    funnelSettings: soft(z.object({
        values: AdsFunnelSettingValuesOut,
        // null — auto-ads ещё не записал ревизию: значения без номера версии
        settingsId: RefId.nullable(),
        revision: z.number().int().nullable(),
    })),
    ...launchV4Fields,
    /**
     * Только ответ `activate` (v4 §8.2): ЭТОТ вызов включил опоздавший запуск
     * (причина статуса `activate-late`) — аудит `diff.late` берёт его, без
     * предчтения. Другие ответы и старый auto-ads — null.
     */
    activatedLate: v4(soft(z.boolean())),
    createdBy: opt(z.string()),
    createdAt: Iso,
    updatedAt: Iso,
});
/**
 * `force: true` — «Включить всё равно» при расхождениях (политика `ads.force_activate`).
 * `acknowledgeLateStart: true` — включить СЕЙЧАС запуск, чей старт уже прошёл
 * (v4 §8.1): auto-ads принимает флаг только у опоздавшего запуска и требует
 * его; сверка не старше 30 мин, иначе 409 `reverify-required`.
 */
export const AdsActivateRequest = z
    .object({ force: z.boolean().optional(), acknowledgeLateStart: z.boolean().optional() })
    .strict();
export const AdsRollbackRequest = z.object({ deleteCreatives: z.boolean().optional() }).strict();
/**
 * Ответ `POST /launches/:id/retry` (202): повтор всегда передаётся пачке
 * запуска (`POST /batches/:id/execute` с этим запуском: перепланирование,
 * проверка опоздания, восстановление) — `via: 'batch'`, `batchId`. Запуск вне
 * пачки auto-ads отклоняет 409 `invalid-state`.
 */
export const AdsLaunchRetryResponse = z.object({
    launchId: RefId,
    state: z.string(),
    batchId: RefId,
    /** `'batch'`. Строка: новое значение не роняет ответ. */
    via: z.string(),
});
/** Ответ `POST /launches/:id/rollback` — задача поставлена в очередь. */
export const AdsLaunchRollbackResponse = z.object({
    launchId: RefId,
    jobId: z.string(),
});
// ---------------------------------------------------------------------------
// Кабинеты и профили — /accounts
//
// Фасад admin-api: строку кабинета собирает admin-api из общего списка
// кабинетов auto-ads (`GET /internal/meta/accounts`) и профилей конструктора
// (`GET /internal/meta-builder/profiles`), карточку — из того же списка и
// `GET /profiles/:pid`. Готовность: `missing = [] ∧ profile.enabled ∧
// platformEnabled`. Токенов кабинета в этих DTO нет и быть не может.
// ---------------------------------------------------------------------------
const MetaNumericId = z.string().regex(/^\d+$/, { message: 'Id Meta — только цифры' });
/** Сколько именованных слотов аудиторий может быть в профиле кабинета. */
export const ADS_AUDIENCE_SLOTS_MAX = 50;
export const ADS_AUDIENCE_SLOT_NAME_MAX = 60;
/** Имя слота аудитории: 1–60 знаков, без «.» и «$» (ключ в Mongo), без пробелов по краям. */
export const AdsAudienceSlotName = z
    .string()
    .min(1, { message: 'Имя слота обязательно' })
    .max(ADS_AUDIENCE_SLOT_NAME_MAX, {
    message: `Имя слота — не длиннее ${ADS_AUDIENCE_SLOT_NAME_MAX} знаков`,
})
    .regex(/^[^.$]+$/, { message: 'Имя слота — без «.» и «$»' })
    .refine((v) => v === v.trim(), { message: 'Имя слота — без пробелов по краям' });
/**
 * Региональные регуляторные данные кабинета (Тайвань, Сингапур, …): категории
 * Meta (`TAIWAN_UNIVERSAL`) и id подтверждённых идентичностей по ролям
 * (`universal_payer` → id).
 */
export const AdsRegionalRegulation = z
    .object({
    categories: z
        .array(z.string().regex(/^[A-Z0-9_]+$/, { message: 'Категория Meta, например TAIWAN_UNIVERSAL' }))
        .min(1, { message: 'Нужна хотя бы одна категория' })
        .max(30),
    identities: z
        .record(z.string().regex(/^[a-z_]+$/, { message: 'Роль латиницей, например universal_payer' }), MetaNumericId)
        .refine((r) => Object.keys(r).length > 0, { message: 'Нужна хотя бы одна идентичность' }),
})
    .strict();
export const AdsAccountProfileInput = z
    .object({
    label: z.string().max(40).optional(),
    pageId: MetaNumericId,
    instagramUserId: MetaNumericId,
    threadsUserId: MetaNumericId.nullable().optional(),
    pixelId: MetaNumericId,
    conversionDomain: z.string().min(1).nullable().optional(),
    defaultLink: z.string().url().nullable().optional(),
    dsaPayor: z.string().min(1).nullable().optional(),
    dsaBeneficiary: z.string().min(1).nullable().optional(),
    regionalRegulation: AdsRegionalRegulation.nullable().optional(),
    /**
     * Именованные слоты аудиторий: имя (любое, его выбирает шаблон) → id
     * custom audience кабинета; null — слот есть, но аудитория не выбрана.
     */
    audienceSlots: z
        .record(AdsAudienceSlotName, MetaNumericId.nullable())
        .refine((r) => Object.keys(r).length <= ADS_AUDIENCE_SLOTS_MAX, {
        message: `Не больше ${ADS_AUDIENCE_SLOTS_MAX} слотов`,
    }),
    /** Час старта кабинета по Лос-Анджелесу, `HH:MM` (режим старта «Час кабинета»). */
    startTimeLocal: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Время — в формате ЧЧ:ММ' })
        .nullable()
        .optional(),
    enabled: z.boolean(),
})
    .strict();
export const AdsAccountProfile = z.object({
    platformAccountId: RefId,
    adAccountId: z.string(),
    label: opt(z.string()),
    pageId: z.string(),
    instagramUserId: z.string(),
    threadsUserId: opt(z.string()),
    pixelId: z.string(),
    conversionDomain: opt(z.string()),
    defaultLink: opt(z.string()),
    dsaPayor: opt(z.string()),
    dsaBeneficiary: opt(z.string()),
    /** Региональные регуляторные данные; чужая форма — null, а не 502. */
    regionalRegulation: soft(z.object({
        categories: z.array(z.string()),
        identities: z.record(z.string(), z.string()),
    })),
    audienceSlots: z.record(z.string(), z.string().nullable()),
    /** Час старта кабинета по Лос-Анджелесу, `HH:MM`. */
    startTimeLocal: opt(z.string()),
    enabled: z.boolean(),
    updatedBy: opt(z.string()),
    updatedAt: Iso,
});
// ---------------------------------------------------------------------------
// Названия сущностей Meta по id — /labels (план meta-economy A10)
// ---------------------------------------------------------------------------
export const ADS_ENTITY_LABEL_KINDS = [
    'page',
    'instagram',
    'pixel',
    'audience',
    'adaccount',
    'post',
];
export const AdsEntityLabelKind = z.enum(ADS_ENTITY_LABEL_KINDS);
/** Не больше ссылок `вид:id` в одном запросе `/labels`. */
export const ADS_ENTITY_LABELS_MAX_REFS = 200;
const LABEL_ID_RE = {
    page: /^\d{1,30}$/,
    instagram: /^\d{1,30}$/,
    pixel: /^\d{1,30}$/,
    audience: /^\d{1,30}$/,
    adaccount: /^(act_)?\d{1,30}$/,
    post: /^\d{1,30}(_\d{1,30})?$/,
};
const AdsEntityLabelRef = z.string().refine((s) => {
    const at = s.indexOf(':');
    const kind = AdsEntityLabelKind.safeParse(s.slice(0, at));
    return at > 0 && kind.success && LABEL_ID_RE[kind.data].test(s.slice(at + 1));
}, { message: 'Ссылка — вид:id' });
/**
 * Название сущности Meta (страница, Instagram, пиксель, аудитория, кабинет,
 * пост) для `EntityLabel`: id показывается всегда, имя — если известно.
 */
export const AdsEntityLabel = z.object({
    kind: AdsEntityLabelKind,
    /** id как его нормализовал auto-ads (кабинет — всегда `act_…`). */
    id: z.string(),
    /** null — имени никто не знает (показывать id). */
    name: z.string().nullable(),
    /** Instagram: ник без `@`. */
    username: opt(z.string()),
    /** Адрес admin-api для `<img src>`; null — аватара нет (инициалы, без запроса). */
    avatarUrl: opt(z.string()),
    /** Откуда имя: platform_accounts | comment_pages | assets | adsets | creatives | cache | meta | none. */
    source: z.string(),
});
/** `?platformAccountId=…&ids=вид:id,вид:id` — токен кабинета, если имени нет в справочниках. */
export const AdsEntityLabelsQuery = z.object({
    platformAccountId: AdsId,
    ids: z
        .string()
        .transform((s) => [
        ...new Set(s
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)),
    ])
        .pipe(z.array(AdsEntityLabelRef).min(1).max(ADS_ENTITY_LABELS_MAX_REFS)),
});
export const AdsEntityLabelsResponse = z.object({ items: z.array(AdsEntityLabel) });
export const AdsAccountRow = z.object({
    platformAccountId: RefId,
    adAccountId: z.string(),
    name: z.string(),
    currency: opt(z.string()),
    timezoneName: opt(z.string()),
    platformEnabled: z.boolean(),
    /** null — профиль ещё не заполнен. */
    profile: AdsAccountProfile.nullable(),
    /** `missing` — ключи полей профиля, без которых запуск невозможен. */
    readiness: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
    /**
     * Названия id профиля (кабинет, страница, Instagram, пиксель, аудитории
     * слотов) — только из справочников auto-ads, без Meta; нет — `[]`.
     */
    labels: z.array(AdsEntityLabel).default([]),
});
const NamedRef = z.object({ id: z.string(), name: opt(z.string()) });
/** Ассеты кабинета из Meta (кэш 10 мин; `?refresh=1` — перечитать). */
export const AdsAccountAssets = z.object({
    account: z.object({
        adAccountId: z.string(),
        name: opt(z.string()),
        currency: opt(z.string()),
        timezoneName: opt(z.string()),
        accountStatus: opt(z.number()),
        defaultDsaPayor: opt(z.string()),
        defaultDsaBeneficiary: opt(z.string()),
    }),
    pages: z.array(NamedRef.extend({ isPublished: opt(z.boolean()) })),
    instagramAccounts: z.array(NamedRef.extend({ username: opt(z.string()) })),
    pixels: z.array(NamedRef.extend({ lastFiredTime: opt(z.string()), isUnavailable: opt(z.boolean()) })),
    customConversions: z
        .array(NamedRef.extend({
        customEventType: opt(z.string()),
        isArchived: opt(z.boolean()),
        isUnavailable: opt(z.boolean()),
    }))
        .default([]),
    minimumBudgets: z.array(z.record(z.string(), z.unknown())).default([]),
    customAudiences: z
        .array(NamedRef.extend({
        subtype: opt(z.string()),
        isValueBased: opt(z.boolean()),
        ratioPct: opt(z.number()),
        approximateCount: opt(z.number()),
    }))
        .default([]),
});
export const AdsAccountDetail = AdsAccountRow.extend({ assets: AdsAccountAssets });
export const AdsAccountListResponse = z.object({ items: z.array(AdsAccountRow) });
// ---------------------------------------------------------------------------
// Пикеры — /posts, /locales, /targeting/interests
// ---------------------------------------------------------------------------
export const AdsPostRow = z.object({
    objectStoryId: z.string(),
    pageId: z.string(),
    postId: z.string(),
    kind: z.enum(['image', 'video', 'unknown']),
    text: z.string().nullable(),
    linkUrl: z.string().nullable(),
    /**
     * Картинка поста: адрес admin-api (`/api/ads/previews/...`), который
     * admin-api подставил вместо пути прокси auto-ads; null — картинки нет.
     */
    previewUrl: opt(z.string()),
    /** Итоги поста как победителя; null — данных нет. */
    winner: z.object({ purchases: Count, cppMinor: Minor.nullable(), windowDays: Count }).nullable(),
    clones: z.object({ total: Count, live: Count, autoClean: Count }),
    lastUsedAt: Iso.nullable(),
    currency: z.string().nullable(),
    /** Из списка какого кабинета пост (запрос по нескольким кабинетам, новый auto-ads). */
    platformAccountId: z.string().nullish().catch(null),
    /**
     * Пост глазами каждого запрошенного кабинета: есть ли он в индексе кабинета
     * (`inIndex`) и совпадает ли его страница со страницей профиля
     * (`pageMatch`, null — у кабинета нет профиля). Новый auto-ads.
     */
    accounts: z
        .array(z.object({
        platformAccountId: z.string(),
        inIndex: z.boolean().nullish().catch(null),
        pageMatch: z.boolean().nullish().catch(null),
    }))
        .nullish()
        .catch(null),
    /** Кабинеты, чья страница совпадает со страницей поста. */
    pageMatchAccountIds: z.array(z.string()).nullish().catch(null),
    /**
     * Совпадает ли страница поста со страницей профиля каждого кабинета
     * (новый auto-ads). Нет — UI сравнивает `pageId` с профилем сам.
     */
    pageMatch: z
        .array(z.object({
        platformAccountId: z.string(),
        matches: z.boolean(),
        pageId: z.string().nullish(),
    }))
        .nullish()
        .catch(null),
});
export const ADS_POST_LIST_MAX_LIMIT = 50;
/** Сколько кабинетов можно запросить в `GET /posts` разом (предел auto-ads). */
export const ADS_POST_LIST_MAX_ACCOUNTS = 20;
/** Значение query: `a,b` или повтор `?k=a&k=b` (Hono отдаёт массив) → список. */
const idListParam = z.preprocess((v) => (Array.isArray(v) ? v : typeof v === 'string' ? [v] : [])
    .flatMap((x) => (typeof x === 'string' ? x.split(',') : []))
    .map((x) => x.trim())
    .filter((x) => x !== ''), z.array(AdsId).max(ADS_POST_LIST_MAX_ACCOUNTS, {
    message: `Не больше ${ADS_POST_LIST_MAX_ACCOUNTS} кабинетов`,
}));
/**
 * `GET /posts`: кабинет (`platformAccountId`) и/или несколько
 * (`platformAccountIds`, через запятую или повтором; ≤ 20) — посты всех
 * кабинетов одним списком, с совпадением страницы по каждому.
 */
export const AdsPostListQuery = z
    .object({
    platformAccountId: AdsId.optional(),
    platformAccountIds: idListParam.optional(),
    q: z.string().trim().max(200).optional(),
    sort: z.enum(['winner', 'recent']).optional(),
    limit: z.coerce.number().int().min(1).max(ADS_POST_LIST_MAX_LIMIT).default(20),
    cursor: z.string().min(1).optional(),
})
    .transform(({ platformAccountId, platformAccountIds, ...rest }) => ({
    ...rest,
    /** Все запрошенные кабинеты, без повторов, в порядке запроса. */
    accountIds: [
        ...new Set([
            ...(platformAccountId !== undefined ? [platformAccountId] : []),
            ...(platformAccountIds ?? []),
        ]),
    ],
}))
    .refine((q) => q.accountIds.length >= 1, {
    message: 'Нужен хотя бы один кабинет',
    path: ['platformAccountIds'],
})
    .refine((q) => q.accountIds.length <= ADS_POST_LIST_MAX_ACCOUNTS, {
    message: `Не больше ${ADS_POST_LIST_MAX_ACCOUNTS} кабинетов`,
    path: ['platformAccountIds'],
});
export const AdsPostListResponse = cursorPage(AdsPostRow);
/** `input` — ссылка на пост, `pageId_postId` или голый postId. */
export const AdsPostResolveQuery = z.object({
    platformAccountId: AdsId,
    input: z.string().trim().min(1).max(500),
});
export const AdsPostResolveWarning = z.object({
    code: z.enum(['page-mismatch', 'clean-scale-exists', 'not-in-index']),
    message: z.string(),
});
export const AdsPostResolveResponse = AdsPostRow.extend({
    found: z.enum(['index', 'meta', 'not_found']),
    warnings: z.array(AdsPostResolveWarning),
});
/**
 * Справочник языков Meta (общий `GET /internal/analytics/locales` auto-ads).
 * Пресеты и запрещённые id — в реестре (`AdsRegistry.localePresets`,
 * `bannedLocaleIds`), здесь их нет: один источник.
 */
export const AdsLocalesResponse = z.object({
    items: z.array(z.object({ localeId: z.number().int(), name: z.string() })),
});
export const AdsInterestSearchQuery = z.object({
    platformAccountId: AdsId,
    q: z.string().trim().min(2).max(80),
});
export const AdsInterest = z.object({
    id: z.string(),
    name: z.string(),
    audienceSizeLower: opt(z.number()),
    audienceSizeUpper: opt(z.number()),
    path: opt(z.array(z.string())),
});
export const AdsInterestListResponse = z.object({ items: z.array(AdsInterest) });
/**
 * «Уже использовали» — интересы наших групп объявлений во всех кабинетах (auto-ads
 * `GET /interests/used`, без Meta). `q` — подстрока названия или начало id; пусто — топ по
 * числу групп.
 */
export const AdsUsedInterestsQuery = z.object({
    q: z.string().trim().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(20),
});
export const AdsUsedInterest = z.object({
    id: z.string(),
    name: z.string(),
    /** В скольких группах объявлений. */
    usageCount: z.number().int().nonnegative(),
    /** В скольких кабинетах. */
    accounts: z.number().int().nonnegative(),
    /** ISO; null — неизвестно. */
    lastUsedAt: z.string().nullable(),
});
export const AdsUsedInterestsResponse = z.object({
    items: z.array(AdsUsedInterest),
    /** Совпадений до `limit`. */
    total: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Пикер кампаний — /campaigns (поиск по базе auto-ads, Meta не вызывается)
// ---------------------------------------------------------------------------
export const ADS_CAMPAIGN_SEARCH_MAX_LIMIT = 50;
export const ADS_CAMPAIGN_SEARCH_DEFAULT_LIMIT = 20;
/** Сколько точных id можно разрешить одним запросом (`ids=`, предел auto-ads). */
export const ADS_CAMPAIGN_SEARCH_MAX_IDS = 50;
export const ADS_CAMPAIGN_SEARCH_MAX_ACCOUNTS = 20;
export const ADS_CAMPAIGN_SEARCH_MAX_STATUSES = 10;
/** Длина поиска (как у auto-ads: длиннее — 400 от него). */
export const ADS_CAMPAIGN_SEARCH_MAX_Q = 100;
/** Id кампании Meta — только цифры. */
export const AdsCampaignId = z.string().regex(/^\d{1,30}$/, {
    message: 'Id кампании — только цифры',
});
/** Список значений query: `a,b` или повтор `?k=a&k=b`, без пустых и повторов. */
const listParam = (item, max, message) => z.preprocess((v) => [
    ...new Set((Array.isArray(v) ? v : typeof v === 'string' ? [v] : [])
        .flatMap((x) => (typeof x === 'string' ? x.split(',') : []))
        .map((x) => x.trim())
        .filter((x) => x !== '')),
], z.array(item).max(max, { message }));
/**
 * `GET /campaigns` — кампании из базы auto-ads для пикера:
 * - `platformAccountIds` — кабинеты (нет — все кабинеты);
 * - `q` — только цифры → префикс id, иначе подстрока названия;
 * - `ids` — точные id (подписи уже выбранных значений);
 * - `statuses` — статусы Meta (`ACTIVE,PAUSED`); порядок — сначала активные, потом свежие.
 */
export const AdsCampaignSearchQuery = z.object({
    platformAccountIds: listParam(AdsId, ADS_CAMPAIGN_SEARCH_MAX_ACCOUNTS, `Не больше ${ADS_CAMPAIGN_SEARCH_MAX_ACCOUNTS} кабинетов`).optional(),
    q: z.string().trim().max(ADS_CAMPAIGN_SEARCH_MAX_Q).optional(),
    ids: listParam(AdsCampaignId, ADS_CAMPAIGN_SEARCH_MAX_IDS, `Не больше ${ADS_CAMPAIGN_SEARCH_MAX_IDS} id`).optional(),
    statuses: listParam(z.string().regex(/^[A-Z_]{1,40}$/, { message: 'Статус — заглавными (ACTIVE)' }), ADS_CAMPAIGN_SEARCH_MAX_STATUSES, `Не больше ${ADS_CAMPAIGN_SEARCH_MAX_STATUSES} статусов`).optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(ADS_CAMPAIGN_SEARCH_MAX_LIMIT)
        .default(ADS_CAMPAIGN_SEARCH_DEFAULT_LIMIT),
});
/**
 * Кампания для пикера. Мягкая форма: обязательны только id кампании и
 * кабинета; остальное — нет, null или чужая форма → null (поле добавят или
 * переименуют в окне выката — список не должен падать в 502).
 */
export const AdsCampaignOption = z.object({
    campaignId: RefId,
    name: soft(z.string()),
    platformAccountId: RefId,
    accountName: soft(z.string()),
    adAccountId: soft(z.string()),
    /** Статус Meta (`ACTIVE`, `PAUSED`, `ARCHIVED`, `DELETED`, …) — строкой. */
    status: soft(z.string()),
    effectiveStatus: soft(z.string()),
    createdTime: soft(Iso),
    updatedTime: soft(Iso),
});
export const AdsCampaignSearchResponse = z.object({ items: z.array(AdsCampaignOption) });
// ---------------------------------------------------------------------------
// Медиатека — /media (спека v3 §2, §8)
// ---------------------------------------------------------------------------
export const AdsMediaKind = z.enum(['image', 'video']);
/** Состояние копии файла в одном кабинете Meta. */
export const AdsMediaState = z.enum([
    'queued',
    'uploading',
    'processing',
    'ready',
    'failed',
    'archived',
]);
/**
 * Состояние копии в ответе — мягко: новое состояние auto-ads показывается как
 * есть, а не роняет весь список в 502.
 */
const MediaStateOut = z.union([AdsMediaState, z.string()]);
/**
 * Копия файла медиатеки в ОДНОМ кабинете. Появляется, когда файл перенесли в
 * кабинет (запуск переносит сам), `meta` — его hash / id видео в этом кабинете.
 */
export const AdsMedia = z.object({
    id: RefId,
    platformAccountId: RefId,
    adAccountId: z.string(),
    state: MediaStateOut,
    meta: z.object({ imageHash: opt(z.string()), videoId: opt(z.string()) }),
    error: opt(AdsErrorInfo),
    updatedAt: Iso,
});
/**
 * Файл медиатеки. Оригинал загруженного файла лежит в S3 (`origin: 'upload'`);
 * файл Meta попадает в медиатеку при первом переносе (`origin: 'meta'`).
 * `previewUrl` — адрес admin-api (`/api/ads/media/assets/:id/preview`), null —
 * превью нет (видео без обложки).
 */
export const AdsAsset = z.object({
    assetId: z.string(),
    kind: AdsMediaKind,
    name: z.string(),
    filename: z.string(),
    contentType: z.string(),
    byteLength: Count,
    width: opt(z.number()),
    height: opt(z.number()),
    durationSec: opt(z.number()),
    origin: z.string(),
    /** `archived` — файл в архиве: оригинал удалён, в новые запуски не выбирается. */
    state: z.union([z.enum(['active', 'archived']), z.string()]),
    previewUrl: opt(z.string()),
    createdBy: opt(z.string()),
    createdAt: Iso,
    byAccount: z.array(AdsMedia),
});
export const ADS_MEDIA_LIST_MAX_LIMIT = 100;
/** `GET /media` — «Загруженные» с копиями по кабинетам (страница «Медиатека»). */
export const AdsMediaListQuery = z.object({
    platformAccountId: AdsId.optional(),
    kind: AdsMediaKind.optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(ADS_MEDIA_LIST_MAX_LIMIT).default(30),
    cursor: z.string().min(1).max(500).optional(),
});
export const AdsMediaListResponse = cursorPage(AdsAsset);
// ---- источники выбора медиа — GET /media/sources -----------------------------
/**
 * Откуда выбирается файл:
 * - `uploaded` — загруженные через нас (медиатека);
 * - `in-ads` — картинки и видео объявлений кабинета (с текстами и ссылкой креатива);
 * - `account-library` — библиотека кабинета Meta, ещё не бывшая в рекламе.
 */
export const AdsMediaSource = z.enum(['uploaded', 'in-ads', 'account-library']);
export const AdsMediaSourcesQuery = z
    .object({
    source: AdsMediaSource,
    /** Обязателен для `in-ads` и `account-library`; для `uploaded` — фильтр. */
    platformAccountId: AdsId.optional(),
    /**
     * Кабинет запуска, ДЛЯ которого выбирают файл (v4 D10): «Уже в рекламе»
     * другого кабинета приходит без ссылки его креатива (`linkOmitted`).
     */
    forPlatformAccountId: AdsId.optional(),
    kind: AdsMediaKind.optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(ADS_MEDIA_LIST_MAX_LIMIT).default(30),
    cursor: z.string().min(1).max(500).optional(),
})
    .refine((q) => q.source === 'uploaded' || q.platformAccountId !== undefined, {
    message: 'Выберите кабинет',
    path: ['platformAccountId'],
});
/** Файл Meta в ответе: кабинет и его hash / id видео. Уходит в пачку как есть. */
export const AdsMediaOrigin = z.object({
    platformAccountId: z.string(),
    imageHash: z.string().optional(),
    videoId: z.string().optional(),
    name: z.string().optional(),
});
/**
 * Элемент выбора медиа — один вид для всех источников. В пачку уходит
 * `{ kind: 'media', assetId, origin? }`. `inAccounts` — где файл уже есть
 * (или переносится); в остальные кабинеты пачки запуск перенесёт его сам.
 */
export const AdsMediaSourceItem = z.object({
    source: z.union([AdsMediaSource, z.string()]),
    assetId: AdsAssetId,
    origin: AdsMediaOrigin.nullish().transform((v) => v ?? null),
    kind: AdsMediaKind,
    name: z.string(),
    /** Адрес admin-api; null — превью нет. */
    previewUrl: opt(z.string()),
    width: opt(z.number()),
    height: opt(z.number()),
    durationSec: opt(z.number()),
    inAccounts: z.array(z.object({ platformAccountId: z.string(), state: MediaStateOut })),
    /** «Уже в рекламе»: тексты, ссылка и кнопка самого нового креатива с этим файлом. */
    creative: soft(z.object({
        primaryText: z.string().optional(),
        headline: z.string().optional(),
        link: z.string().optional(),
        cta: z.string().optional(),
        /**
         * Ссылку креатива auto-ads не отдал: файл выбирают для запуска в ДРУГОМ
         * кабинете (`other-account`, v4 D10). Строка: новое значение не роняет ответ.
         */
        linkOmitted: z.string().nullish().catch(null),
    })),
    /** «Уже в рекламе»: в скольких креативах был файл и когда последний раз. */
    usage: soft(z.object({ creatives: Count, lastUsedAt: Iso.nullable() })),
    createdAt: opt(Iso),
});
export const AdsMediaSourcesResponse = cursorPage(AdsMediaSourceItem);
// ---- загрузка: браузер → S3 частями, admin-api проводит только JSON ---------
/** Предел `sha256` для дедупликации — 64 hex всего файла. */
const Sha256Hex = z.string().regex(/^[a-f0-9]{64}$/, { message: 'sha256 — 64 hex' });
/** Пределы фактов видео из браузера (как у auto-ads, решения 134–137). */
export const ADS_MEDIA_MAX_SIDE_PX = 16_384;
export const ADS_MEDIA_MAX_DURATION_SEC = 14_400;
/**
 * Начало загрузки. Типы и пределы проверяет auto-ads (415
 * `unsupported-media-type`, 413 `upload-too-large`; пределы — `/health` →
 * `mediaMaxBytes`). `sha256` всего файла (браузер считает для файлов ≤ 200 МБ):
 * тот же файл уже в медиатеке — загрузки нет, сразу `{deduplicated: true, asset}`.
 * Кабинеты не передаются: файл переносится в кабинет запуска сам.
 */
export const AdsUploadInitRequest = z
    .object({
    kind: AdsMediaKind,
    filename: z
        .string()
        .min(1)
        .max(255)
        .regex(/\.[A-Za-z0-9]{1,8}$/, { message: 'Имя файла — с расширением' }),
    contentType: z.string().min(1).max(100),
    byteLength: z.number().int().positive(),
    name: z.string().max(120).optional(),
    sha256: Sha256Hex.optional(),
    /**
     * Что браузер прочитал из видео (v5 N7): медиатека показывает сразу,
     * Meta после обработки заменяет своими. Только у видео; ширина и высота —
     * парой.
     */
    width: z.number().int().min(1).max(ADS_MEDIA_MAX_SIDE_PX).optional(),
    height: z.number().int().min(1).max(ADS_MEDIA_MAX_SIDE_PX).optional(),
    durationSec: z.number().positive().max(ADS_MEDIA_MAX_DURATION_SEC).optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if ((b.width === undefined) !== (b.height === undefined)) {
        ctx.addIssue({ code: 'custom', path: ['width'], message: 'Ширина и высота — парой' });
    }
    if (b.kind !== 'video' && (b.width !== undefined || b.durationSec !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['kind'],
            message: 'Размеры и длительность — только у видео',
        });
    }
});
/** Подписанная ссылка на часть `partNumber` (с 1): браузер PUT'ит байты прямо в S3. */
export const AdsUploadPartUrl = z.object({
    partNumber: z.number().int().min(1),
    size: z.number().int().positive(),
    url: z.string().url(),
});
export const AdsUploadInitResponse = z.discriminatedUnion('deduplicated', [
    z.object({
        deduplicated: z.literal(false),
        uploadId: RefId,
        assetId: z.string(),
        partSize: z.number().int().positive(),
        partCount: z.number().int().positive(),
        parts: z.array(AdsUploadPartUrl),
        /** Срок сессии загрузки (ссылки истекают раньше — их обновляет `/parts`). */
        expiresAt: Iso,
    }),
    z.object({ deduplicated: z.literal(true), asset: AdsAsset }),
]);
/** Свежие ссылки на части (истёк срок подписи); без `partNumbers` — на все. */
export const AdsUploadPartsRequest = z
    .object({
    partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(10_000).optional(),
})
    .strict();
export const AdsUploadPartsResponse = z.object({ parts: z.array(AdsUploadPartUrl) });
/** Завершение: `ETag` каждой части из ответа S3. */
export const AdsUploadCompleteRequest = z
    .object({
    parts: z
        .array(z
        .object({
        partNumber: z.number().int().min(1).max(10_000),
        etag: z.string().min(1).max(200),
    })
        .strict())
        .min(1)
        .max(10_000),
})
    .strict();
export const AdsUploadCompleteResponse = z.object({ asset: AdsAsset });
// ---------------------------------------------------------------------------
// Запросы деталей кабинета
// ---------------------------------------------------------------------------
/** `GET /accounts/:pid?refresh=1` — перечитать ассеты из Meta мимо кэша. */
export const AdsAccountDetailQuery = z.object({
    refresh: z
        .enum(['0', '1'])
        .optional()
        .transform((v) => v === '1'),
});
// ---------------------------------------------------------------------------
// Каталог и история настроек воронки — /funnel-settings (план K6)
// ---------------------------------------------------------------------------
/** Код отказа auto-ads «каталог настроек недоступен» (app-service не ответил). */
export const ADS_FUNNEL_SETTINGS_CATALOG_UNAVAILABLE = 'funnel-settings-catalog-unavailable';
/**
 * Виды настроек: `enum` / `funnelConfig` — один из `options`, `boolean`,
 * `number`, `trialPlans` — набор тарифов из `options` (значение-список,
 * спека 2026-10-26 «Варианты триала»).
 */
export const ADS_FUNNEL_SETTING_KINDS = [
    'enum',
    'boolean',
    'number',
    'funnelConfig',
    'trialPlans',
];
export const AdsFunnelSettingKnownKind = z.enum(ADS_FUNNEL_SETTING_KINDS);
/**
 * Вид настройки в ответе — мягкий: новый вид каталога не роняет ответ
 * (экран показывает такую настройку только для чтения).
 */
export const AdsFunnelSettingKind = z.union([AdsFunnelSettingKnownKind, z.string().min(1)]);
export const AdsFunnelSettingOption = z.object({ value: z.string(), label: z.string() });
/** Одна настройка каталога (K1 app-service, через auto-ads). */
export const AdsFunnelSettingDef = z.object({
    key: z.string().min(1),
    kind: AdsFunnelSettingKind,
    /** `client` — применяет воронка, `server` — app-service (тарифы и т. п.). */
    applies: z.string(),
    /** В каких воронках действует; `'*'` — во всех. */
    funnels: z.union([z.literal('*'), z.array(z.string())]),
    /** Какие экраны / места воронки она меняет (справочно). Нет — null. */
    controls: soft(z.object({
        screens: z.array(z.string()).optional(),
        placements: z.array(z.string()).optional(),
    })),
    /** Название для админки (рус.). */
    label: z.string(),
    /** Описание для MCP (англ.). */
    description: z.string(),
    /** На каких уровнях задаётся (сейчас только `campaign`, решение Р3). */
    levels: z.array(z.string()),
    /** Варианты `enum` / `funnelConfig` / `trialPlans` (тарифы): значение и подпись. Нет — null. */
    options: soft(z.array(AdsFunnelSettingOption)),
    /**
     * Значение может быть с условиями (план C2). Нет (старый каталог) — false:
     * только обычное значение.
     */
    targetable: z
        .boolean()
        .nullish()
        .catch(null)
        .transform((v) => v === true),
    /**
     * Список значений сейчас недоступен (например, тарифы триала не загрузились):
     * `options` пуст, значение сохраняется как есть. Нет — false.
     */
    unavailable: z
        .boolean()
        .nullish()
        .catch(null)
        .transform((v) => v === true),
});
/** Измерение условий значений (план C2). */
export const AdsFunnelSettingDimension = z.object({
    key: z.string().min(1),
    /** `iso2[]` — коды стран ISO-2; нет — значения из `values`. */
    type: opt(z.string()),
    values: opt(z.array(z.string())),
});
export const AdsFunnelSettingsCatalog = z.object({
    /** Хэш каталога — входит в `previewHash` пачки. */
    catalogVersion: z.string(),
    /** true — app-service не ответил, отдана последняя удачная копия. */
    stale: z
        .boolean()
        .nullish()
        .transform((v) => v === true),
    settings: z.array(AdsFunnelSettingDef),
    /**
     * Измерения условий (план C2): `countries` (`type: 'iso2[]'`), `ageRanges`,
     * `genders` (закрытые `values`). Нет (старый каталог) — пустой список:
     * условия задать нельзя.
     */
    dimensions: z
        .array(AdsFunnelSettingDimension)
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
});
const MetaIdRe = /^\d{6,25}$/;
/**
 * `GET /funnel-settings?launchId=|campaignId=` — ровно один параметр:
 * запуск (id конструктора) или кампания Meta (цифры).
 */
export const AdsCampaignFunnelSettingsQuery = z
    .object({
    launchId: AdsId.optional(),
    campaignId: z.string().regex(MetaIdRe, { message: 'Неверный id кампании' }).optional(),
})
    .strict()
    .refine((q) => (q.launchId === undefined) !== (q.campaignId === undefined), {
    message: 'Нужен ровно один параметр: launchId или campaignId',
});
/** Откуда взялась версия настроек: запуск, ручная правка, агент, наследование SCALE. */
export const AdsFunnelSettingsRevisionSource = z.object({
    kind: z.string(),
    launchId: opt(z.string()),
    batchId: opt(z.string()),
    templateId: opt(z.string()),
    templateVersion: opt(z.number().int()),
    parentRevisionId: opt(z.string()),
});
/** Версия (ревизия) настроек. Автор (`createdBy`) auto-ads наружу не отдаёт. */
export const AdsFunnelSettingsRevision = z.object({
    revisionId: RefId,
    revision: z.number().int(),
    values: AdsFunnelSettingValuesOut,
    catalogVersion: z.string(),
    source: soft(AdsFunnelSettingsRevisionSource),
    createdAt: Iso,
    /**
     * S3 (правка настроек живой кампании): окно действия версии. Все поля необязательные и БЕЗ значений
     * по умолчанию: старый auto-ads (или выключенный флаг) их не шлёт — ответ admin-api остаётся
     * прежним, экран читает «нет поля» как «нет».
     */
    effectiveFrom: Iso.nullish().catch(undefined),
    effectiveTo: Iso.nullish().catch(undefined),
    /** Причина правки (пишет человек или нейросеть); у запуска её нет. */
    reason: z.string().nullish().catch(undefined),
    /** Через что внесена правка: `admin` — админка, `mcp` — нейросеть. */
    via: z.string().nullish().catch(undefined),
    /** Ключи настроек, изменённые этой версией. */
    changedKeys: z.array(z.string()).nullish().catch(undefined),
    /** Id версии, к которой вернулись (откат). */
    revertOf: z.string().nullish().catch(undefined),
    /** Кто внёс правку (e-mail) — отдаётся только админке, нейросеть его не видит. */
    createdBy: z.string().nullish().catch(undefined),
});
/** Текущая версия + документ настроек (уровень, объект Meta, происхождение). */
export const AdsFunnelSettingsCurrent = AdsFunnelSettingsRevision.extend({
    settingsId: RefId,
    /** `active` / `tombstoned` (запуск откачен). */
    state: z.string(),
    level: z.string(),
    /** id объекта Meta; null — объект ещё не создан. */
    metaId: opt(z.string()),
    platformAccountId: z.string(),
    origin: soft(z.object({
        kind: z.string(),
        launchId: opt(z.string()),
        scaleDecisionId: opt(z.string()),
    })),
});
export const AdsCampaignFunnelSettings = z.object({
    /** null — у запуска / кампании настроек нет. */
    current: AdsFunnelSettingsCurrent.nullable(),
    /** История версий (новые auto-ads отдают не больше 100). */
    revisions: z.array(AdsFunnelSettingsRevision),
    /** Сколько версий действует всего (без брошенных; номера могут идти с пропусками). Нет — `null`. */
    revisionsTotal: Count.nullish().catch(undefined),
    /**
     * Правка настроек воронки включена на сервере рекламы (`CAMPAIGN_SETTINGS_EDIT_ENABLED`). Нет поля
     * (старый auto-ads) — считаем выключенной: кнопок правки нет.
     */
    editEnabled: z.boolean().nullish().catch(undefined),
});
// ---------------------------------------------------------------------------
// Тело ошибки `/api/ads/*`
// ---------------------------------------------------------------------------
/**
 * Машинные коды ошибок раздела (`error` в теле):
 * - `ads_not_configured` (503) — admin-api без `AUTO_ADS_API_*`;
 * - `ads_builder_disabled` (503) — конструктор выключен флагом в auto-ads;
 * - `upstream_rejected` (статус auto-ads: 400/404/409/410/413/415/422/429;
 *   `meta-error` — 502) — отказ; `code` — код auto-ads, `message` — RU,
 *   `issues` — проблемы значений под поля формы, `details` — данные для
 *   действия экрана;
 * - `upstream_unavailable` (502) — auto-ads недоступен / прислал не то;
 * - картинка (превью): `preview_not_found` (404);
 * - пути: `invalid_id` (400).
 */
export const AdsErrorDetails = z.object({
    /** `template-version-conflict` — текущая версия шаблона. */
    currentVersion: opt(Count),
    /** `preview-stale` — хеш актуального preview. */
    previewHash: opt(z.string()),
    /** `idempotency-key-taken` — уже созданная пачка. */
    batchId: opt(z.string()),
    launchId: opt(z.string()),
    /** `invalid-state` — текущее состояние и допустимые. */
    state: opt(z.string()),
    /** `invalid-state` повтора запуска пачки — состояние пачки. */
    batchState: opt(z.string()),
    /** `start-passed` — время старта, которое уже прошло (ISO). */
    startTime: opt(z.string()),
    /**
     * `start-passed` (v4 §4.6): запуск можно включить сейчас с подтверждением
     * (`acknowledgeLateStart`) и нужен ли для этого `force` (расхождения сверки).
     */
    canActivateLate: v4(opt(z.boolean())),
    needsForce: v4(opt(z.boolean())),
    /** `reverify-required` — когда была последняя сверка (null — не было) и её предел, мин. */
    verifiedAt: v4(opt(z.string())),
    maxAgeMinutes: v4(opt(Count)),
    allowed: opt(z.array(z.string())),
    /** `upload-incomplete` — номера недостающих частей (с 1). */
    missing: opt(z.array(Count)),
    /**
     * `rollback-forbidden-active` — что включено в Meta (откат только после
     * паузы): вид объекта, id, статусы.
     */
    active: opt(z.array(z.object({
        kind: z.string(),
        metaId: z.string(),
        effectiveStatus: opt(z.string()),
        status: opt(z.string()),
    }))),
    /** `revision-conflict` (S3) — текущая версия настроек воронки. */
    currentRevision: opt(Count),
    /** `too-frequent` (S3) — когда следующая правка возможна (ISO). */
    nextAllowedAt: opt(z.string()),
    /** `daily-cap-reached` (S3) — чей лимит: campaign | total. */
    scope: opt(z.string()),
    /** `rate-limited` — через сколько повторить (ответ несёт и `Retry-After`). */
    retryAfterMs: opt(Count),
    /** `verify-has-errors` — число расхождений-ошибок. */
    errorCount: opt(Count),
    /** `list-in-use` — правила SCALE, которые держат список. */
    rules: opt(z.array(z.object({ id: z.string(), name: z.string().nullable() }))),
    /** `meta-error` — ответ Meta для раскрывашки (`userMsg`, `fbtraceId`). */
    metaError: opt(z.object({
        code: opt(z.union([z.string(), z.number()])),
        subcode: opt(z.number()),
        title: opt(z.string()),
        userMsg: opt(z.string()),
        fbtraceId: opt(z.string()),
        transient: opt(z.boolean()),
        /** Пути виноватых полей Meta (`[['targeting','age_max']]`). */
        blame: opt(z.array(z.array(z.string()))),
    })),
});
export const AdsErrorResponse = z.object({
    error: z.string(),
    code: opt(z.string()),
    message: opt(z.string()),
    issues: opt(z.array(AdsValueIssue)),
    details: opt(AdsErrorDetails),
});
//# sourceMappingURL=ads-builder.js.map